import {
  forwardRef, useEffect, useMemo, useRef, useState,
  type DragEvent,
} from "react";
import { createPortal } from "react-dom";
import { addDays, format } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { can } from "../lib/permissions";
import { useEffectiveUser } from "../lib/auth";
import { useBetaDesign } from "../lib/platform";
import { ymd, isSameYmd } from "../lib/date";
import type { Assignment, Employee, Shift, ShiftCode } from "../lib/types";
import { TIME_OFF_CODE } from "../lib/types";
import { compareBySeniority } from "../lib/rank";
import { Button } from "../components/ui/Button";
import { FixCoverageModal } from "../components/FixCoverageModal";
import { OnCallTodayStrip } from "../components/OnCallTodayStrip";
import { extendedHomeRange, shiftWrapsMidnight } from "../lib/coverage";
import { readableTextOn } from "../lib/colors";
import { applyTacOverlay, visibleShiftsAcross, isTacActiveOn, isShiftActiveOn, effectiveHomeShiftFor } from "../lib/tacMode";
import { buildEffectiveShiftLookup } from "../lib/shiftAssignments";
import { generateSchedulePdf, type ScheduleGroup } from "../lib/schedulePdf";

// 2-week pay-period schedule, grouped by shift.
//
// Interactions:
//   - Click any cell → inline text input for a code (SA, C, 9-4, XXX, …).
//     Enter saves. Esc cancels. Blank + Enter removes. Tab moves to next cell.
//   - Drag an employee's name row and drop it on another shift section's
//     header to change that employee's home shift.
//   - Drag a filled cell and drop it on another cell to move the
//     assignment (preserves shift + code, just changes the date).
//
// Each employee belongs to one section (their home shift). An assignment's
// shift defaults to that home shift; the `note` field holds the free text
// code displayed in the cell. Plain XXX (no custom code) displays as
// "XXX" automatically.

const DRAG_EMPLOYEE = "application/x-sm-employee";
const DRAG_ASSIGNMENT = "application/x-sm-assignment";

// (Cell-editing state lives at the page level as `picker`; cells just emit
// click events back up through onOpenPicker.)

export function Schedule() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const mayEditCells = can.editCells(me);
  const mayEditRoster = can.editRoster(me);
  const maySetAnchor = can.setScheduleAnchor(me);
  const mayViewAudit = can.viewAudit(me);
  const nav = useNavigate();
  const { deptId } = useParams<{ deptId?: string }>();

  // The "fix coverage" modal — populated when a supervisor clicks a
  // red Coverage or OIC cell on a shift section. Also fired from an
  // OT × delete on the Section's OT row.
  const [fixCoverage, setFixCoverage] = useState<{ shiftId: string; date: string } | null>(null);

  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const activeDept = useMemo(
    () => (departments.data ?? []).find((d) => d.id === deptId)
       ?? (departments.data ?? [])[0]
       ?? null,
    [departments.data, deptId],
  );

  // If no deptId was given in the URL but departments are loaded, redirect
  // to the first (default) department so the URL is stable.
  const needsRedirect = !deptId && activeDept !== null;

  // The shared "default" anchor lives on the agency row so every
  // user in every browser lands on the same pay period by default.
  // Owner / L1 can change that shared default via the date picker
  // (calls api.setScheduleAnchor). Every user — L1 through L4 — can
  // additionally scroll locally via Previous / Today / Next; those
  // updates stay in this browser only and don't touch the shared
  // anchor, so a junior supervisor browsing ahead doesn't jerk
  // everyone else's view.
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const serverAnchorStr = agency.data?.scheduleAnchorDate ?? null;
  const serverAnchor = useMemo(() => {
    if (serverAnchorStr) {
      const [y, m, dd] = serverAnchorStr.split("-").map(Number);
      if (y && m && dd) {
        const d = new Date(y, m - 1, dd);
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [serverAnchorStr]);

  // Null = follow the server's shared anchor; non-null = this user
  // has scrolled forward / back locally and is off the shared
  // default. Reset to null with the date picker (Owner/L1 only —
  // which also moves everyone else's shared default to that date).
  const [localAnchor, setLocalAnchor] = useState<Date | null>(null);
  const anchor = localAnchor ?? serverAnchor;

  // iOS beta design: pins the schedule to a 7-day rolling window
  // (yesterday → today → next 5) instead of the standard 28-day
  // pay-period grid. Officer rows are condensed to initials and
  // radio numbers below. Owner-only and iOS-app-only — every
  // other surface keeps the 28-day view.
  const betaDesign = useBetaDesign();
  // Page offset in beta mode. 0 = the default "today's week"
  // window (yesterday + today + next 5). Swiping left bumps to
  // the next week (+1), swiping right to the previous week (-1).
  // Reset to 0 whenever the beta flag flips or the page mounts.
  const [betaWeekOffset, setBetaWeekOffset] = useState(0);
  useEffect(() => {
    setBetaWeekOffset(0);
  }, [betaDesign]);

  // On first mount (and on hard refresh), jump straight to the
  // 28-day page that contains today, regardless of where the
  // shared agency anchor sits. The shared anchor is typically the
  // first day of a pay period months in the past, so without this
  // the schedule opens to that ancient page and the supervisor has
  // to hit "Today" every time. Once they navigate with Previous /
  // Next, the ref locks the jump so it doesn't fire again until a
  // remount (route change away and back, or refresh).
  const didTodayJump = useRef(false);
  useEffect(() => {
    if (didTodayJump.current) return;
    if (!agency.data) return; // wait for the anchor to load
    didTodayJump.current = true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const msPerDay = 86_400_000;
    const diff = Math.floor((today.getTime() - serverAnchor.getTime()) / msPerDay);
    const pages = Math.floor(diff / 28);
    if (pages !== 0) setLocalAnchor(addDays(serverAnchor, pages * 28));
  }, [agency.data, serverAnchor]);

  const setAnchorMut = useMutation({
    mutationFn: (date: Date) => api.setScheduleAnchor(ymd(date)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency"] }),
  });
  // Date-picker set: Owner / L1 only, writes the shared anchor and
  // drops the local override so this browser follows the new
  // default too.
  function setSharedAnchor(d: Date) {
    if (!maySetAnchor) return;
    setLocalAnchor(null);
    setAnchorMut.mutate(d);
  }

  // Deep-link params for "→ cell" jumps from the Audit page. The
  // useEffect that acts on these lives further down, after
  // setSelection is declared — read them up here so both they and
  // setSearchParams stay in one place.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get("focus");
  const anchorParam = searchParams.get("anchor");

  const employees = useQuery({
    queryKey: ["employees", activeDept?.id],
    queryFn: () => api.listEmployees(activeDept ? { departmentId: activeDept.id } : undefined),
    enabled: activeDept !== null,
  });
  const shifts = useQuery({
    queryKey: ["shifts", activeDept?.id],
    queryFn: () => api.listShifts(activeDept ? { departmentId: activeDept.id } : undefined),
    enabled: activeDept !== null,
    // Always refetch on mount so a toggle to a shift's settings
    // (requiresOic, minStaffing, etc.) on the Shifts page is
    // reflected the next time the supervisor opens this schedule —
    // no waiting for staleTime to expire.
    refetchOnMount: "always",
  });
  const shiftCodes = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  const seniorityMode = useQuery({
    queryKey: ["seniorityMode"],
    queryFn: api.getSeniorityMode,
  });
  const payWeekendWeeks = useQuery({
    queryKey: ["payWeekendWeeks"],
    queryFn: api.getPayWeekendWeeks,
  });
  useEffect(() => {
    setPayWeekendWeeksForChrome(payWeekendWeeks.data ?? [0, 2]);
  }, [payWeekendWeeks.data]);

  // Tac-mode overlay. Non-destructive: filters shifts to the right
  // set for the current tac state and projects flagged officers
  // onto their tac home shift while tac mode is active. The raw
  // queries are still available downstream for anything that needs
  // unprojected data; everything that builds the grid layout reads
  // through the *Effective* aliases below.
  const days = useMemo(() => {
    if (betaDesign) {
      // Beta: 7-day rolling window. Offset 0 = yesterday + today
      // + next 5; offset 1 = the next week; offset -1 = the
      // previous week. Swipe handlers on the scroll container
      // mutate betaWeekOffset.
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Array.from(
        { length: 7 },
        (_, i) => addDays(today, i - 1 + betaWeekOffset * 7),
      );
    }
    return Array.from({ length: 28 }, (_, i) => addDays(anchor, i));
  }, [anchor, betaDesign, betaWeekOffset]);
  const startStr = ymd(days[0]);
  const endStr = ymd(days[days.length - 1]);
  // Date-aware tac overlay. effectiveShifts is the union of every
  // shift visible on ANY day of the window — for a page that
  // straddles tac mode's start date that's both shift families
  // simultaneously, and the per-cell sectionOwnsCell gate hides
  // the days each section doesn't own. effectiveEmployees is the
  // legacy projection (applyTacOverlay) used by downstream lookups
  // that don't yet support date-aware lookups (e.g. roster
  // grouping fallbacks); the per-cell rendering walks
  // effectiveHomeShiftFor for date-by-date precision.
  const dayStrs = useMemo(() => days.map((d) => ymd(d)), [days]);
  const tacOverlay = useMemo(
    () => applyTacOverlay(agency.data, shifts.data ?? [], employees.data ?? []),
    [agency.data, shifts.data, employees.data],
  );
  const effectiveShifts = useMemo(
    () => visibleShiftsAcross(agency.data, shifts.data ?? [], dayStrs),
    [agency.data, shifts.data, dayStrs],
  );
  const effectiveEmployees = tacOverlay.effectiveEmployees;
  const today = new Date();

  const assignments = useQuery({
    queryKey: ["assignments", startStr, endStr],
    queryFn: () => api.listAssignments(startStr, endStr),
  });

  // Approved time-off overlays: fetch the range once, pick
  // approved records, expand into a (empId|date) → code map used
  // by the home-cell renderer as a fall-through below assignments
  // and extRange but above empty. A denied or requested record is
  // intentionally ignored.
  const timeOff = useQuery({
    queryKey: ["timeoff", startStr, endStr],
    queryFn: () => api.listTimeOff(startStr, endStr),
  });
  const timeOffAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of timeOff.data ?? []) {
      if (t.status !== "approved") continue;
      const start = new Date(t.startDate + "T00:00:00");
      const end = new Date(t.endDate + "T00:00:00");
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        m.set(`${t.employeeId}|${ymd(d)}`, TIME_OFF_CODE[t.type]);
      }
    }
    return m;
  }, [timeOff.data]);

  // IOD periods for the visible window. Materialiser already writes
  // IOD assignment rows for shift-assigned officers; this overlay is
  // for UNASSIGNED officers (no home / tac home) — their row in the
  // grid has no shift to write rows against, so the cells fall back
  // to this map to render the IOD chip when a period covers the
  // date. Pure visual overlay; coverage doesn't include unassigned
  // officers anyway so no double-counting concerns.
  const iodPeriods = useQuery({
    queryKey: ["iodPeriods", startStr, endStr],
    queryFn: () => api.listIodPeriods(startStr, endStr),
  });
  const iodOverlayAt = useMemo(() => {
    const m = new Map<string, true>();
    for (const p of iodPeriods.data ?? []) {
      let cur = p.startDate > startStr ? p.startDate : startStr;
      const end = p.endDate && p.endDate < endStr ? p.endDate : endStr;
      while (cur <= end) {
        m.set(`${p.employeeId}|${cur}`, true);
        const d = new Date(cur + "T00:00:00");
        d.setDate(d.getDate() + 1);
        cur = d.toISOString().slice(0, 10);
      }
    }
    return m;
  }, [iodPeriods.data, startStr, endStr]);

  // Days-off pattern overlay for UNASSIGNED officers. Once they're
  // moved onto a shift (homeShiftId or tacHomeShiftId set), the
  // pattern is materialised onto real assignment rows by
  // applyDaysOffPattern and this overlay no longer applies. While
  // they're unassigned, the schedule grid still wants to show
  // their XXX cells so the supervisor sees the planned days off —
  // we compute them on the fly from the same weekday set the
  // materialiser would have written.
  const patternOverlayAt = useMemo(() => {
    const m = new Map<string, true>();
    for (const e of employees.data ?? []) {
      if (e.homeShiftId || e.tacHomeShiftId) continue; // shift-assigned → covered by materialiser
      if (!e.daysOffStartDate) continue;
      const isTac = e.daysOffMode === "tac";
      const w1 = (isTac ? e.tacDaysOffWeekdays : e.daysOffWeekdays) ?? [];
      const w2 = isTac ? e.tacDaysOffWeekdaysW2 : e.daysOffWeekdaysW2;
      if (w1.length === 0) continue;
      const w1Set = new Set(w1);
      const w2Set = w2 && w2.length > 0 ? new Set(w2) : null;
      const startMs = new Date(e.daysOffStartDate + "T00:00:00").getTime();
      for (const d of days) {
        const dStr = ymd(d);
        if (dStr < e.daysOffStartDate) continue;
        if (e.retirementDate && dStr >= e.retirementDate) continue;
        const wd = d.getDay();
        let activeSet: Set<number> = w1Set;
        if (w2Set) {
          const daysSince = Math.floor((d.getTime() - startMs) / 86_400_000);
          activeSet = Math.floor(daysSince / 7) % 2 === 0 ? w1Set : w2Set;
        }
        if (activeSet.has(wd)) {
          m.set(`${e.id}|${dStr}`, true);
        }
      }
    }
    return m;
  }, [employees.data, days]);

  const coverage = useQuery({
    // Department id is part of the key so switching schedules
    // doesn't serve the previous department's cached coverage. The
    // employees / shifts queries upstream already key on deptId; the
    // coverage query was the odd one out, which is why a click from
    // Patrol → Dispatch (or vice versa) showed stale or empty
    // chips until the next refetch.
    queryKey: ["coverage", activeDept?.id, startStr, endStr],
    queryFn: () => api.coverage(startStr, endStr,
      activeDept ? { departmentId: activeDept.id } : undefined),
    enabled: activeDept !== null,
    // Same reason as the shifts query above: opening a schedule
    // recomputes coverage so freshly-edited shift / employee /
    // requires-OIC settings show through immediately.
    refetchOnMount: "always",
  });

  // Temporary shift placements that override homeShiftId for a
  // window. Fetched for the visible 4-week range so the grid can
  // surface an officer under whichever shift they're actually on
  // each day. Range filter on the query side keeps it cheap.
  const shiftAssignments = useQuery({
    queryKey: ["shiftAssignments", startStr, endStr],
    queryFn: () => api.listShiftAssignments(startStr, endStr),
  });
  const shiftLookup = useMemo(
    () => buildEffectiveShiftLookup(
      // Use RAW employees here, not effectiveEmployees — the lookup
      // wants the unprojected homeShiftId / tacHomeShiftId pair so
      // it can pick per date based on the tac transition. Passing
      // the already-projected list would collapse the two values
      // and lose the regular-then-tac transition on a straddling
      // window.
      employees.data ?? [],
      shiftAssignments.data ?? [],
      agency.data,
      dayStrs,
    ),
    [employees.data, shiftAssignments.data, agency.data, dayStrs],
  );

  const coverageByShiftDate = useMemo(() => {
    const m = new Map<string, {
      assigned: number; minStaffing: number;
      oicAssigned: number; oicRequired: number;
      overridden: boolean;
    }>();
    for (const c of coverage.data ?? []) {
      m.set(`${c.shiftId}|${c.date}`, {
        assigned: c.assigned,
        minStaffing: c.minStaffing,
        oicAssigned: c.oicAssigned,
        oicRequired: c.oicRequired,
        overridden: c.overridden,
      });
    }
    return m;
  }, [coverage.data]);

  // Home-shift cell map: for each (employee, date) pair, pick the
  // assignment we should render in the employee's row. Prefer one
  // that matches their current home shift; fall back to any
  // assignment that day so a stale off-code (e.g. XXX on a shift the
  // employee no longer belongs to) still surfaces in the UI instead
  // of silently disappearing. Cross-shift *overtime* rows are kept in
  // otByShiftDate below, not here.
  const employeesById = useMemo(
    () => new Map(effectiveEmployees.map((e) => [e.id, e])),
    [effectiveEmployees],
  );
  const shiftCodeByCode = useMemo(
    () => new Map((shiftCodes.data ?? []).map((c) => [c.code.toUpperCase(), c])),
    [shiftCodes.data],
  );
  const assignmentAt = useMemo(() => {
    const m = new Map<string, Assignment>();
    for (const a of assignments.data ?? []) {
      const emp = employeesById.get(a.employeeId);
      if (!emp || emp.homeShiftId !== a.shiftId) continue;
      m.set(`${a.employeeId}|${a.date}`, a);
    }
    return m;
  }, [assignments.data, employeesById]);

  // Fallback for stale cross-shift rows whose code isn't OT (e.g. an
  // XXX left behind after an employee's home_shift_id changed). We
  // still want to surface them in the employee's home cell so they
  // don't silently disappear.
  //
  // OT / OT½ rows are intentionally excluded — those are already
  // represented by the consolidated OT row + the extended-hours
  // chip on the covering officer's home cell. Letting them through
  // here caused a literal "OT½" text chip to render on a covering
  // officer's home cell when the extended-hours chip lived on a
  // different date (first-half Night→Day covers put the chip on
  // the previous calendar day).
  const crossAssignmentAt = useMemo(() => {
    const m = new Map<string, Assignment>();
    for (const a of assignments.data ?? []) {
      const emp = employeesById.get(a.employeeId);
      if (!emp || emp.homeShiftId === a.shiftId) continue;
      const noteUpper = (a.note ?? "").trim().toUpperCase();
      if (noteUpper === "OT" || noteUpper === "OT½" || noteUpper === "OT/2") continue;
      const key = `${a.employeeId}|${a.date}`;
      if (!m.has(key)) m.set(key, a);
    }
    return m;
  }, [assignments.data, employeesById]);

  // Cross-shift OT rows: only count an assignment as overtime when
  // its code resolves to a positive coverage contribution. A stale
  // off-code (XXX, S, V…) on the "wrong" shift isn't overtime — it's
  // an orphan that should render on the employee's home row (above),
  // not as a fake OT helper on some other shift.
  const otByShiftDate = useMemo(() => {
    const m = new Map<string, Assignment[]>();
    for (const a of assignments.data ?? []) {
      const emp = employeesById.get(a.employeeId);
      if (!emp || emp.homeShiftId === a.shiftId) continue;
      const note = (a.note ?? "").trim().toUpperCase();
      const cov = note ? shiftCodeByCode.get(note)?.coverageValue : undefined;
      // Blank note defaults to 1.0 (someone showing up to work);
      // known code must have coverage > 0 to count; unknown text
      // (custom string) is treated as working.
      const contributing = !note || cov === undefined || cov > 0;
      if (!contributing) continue;
      const key = `${a.shiftId}|${a.date}`;
      const bucket = m.get(key);
      if (bucket) bucket.push(a);
      else m.set(key, [a]);
    }
    return m;
  }, [assignments.data, employeesById, shiftCodeByCode]);

  const shiftById = useMemo(
    () => Object.fromEntries((shifts.data ?? []).map((s) => [s.id, s])),
    [shifts.data],
  );

  // For each (employee, date) where the employee is doing OT on a
  // neighboring shift, compute the extended worked range to display
  // in their HOME-shift cell. Format: "21-11" (24h, no minutes).
  //
  // Also builds a parallel map from chip cell → the underlying OT
  // assignments so the clear-cell path can delete them even when
  // the chip lives on a different calendar day than the OT row
  // (first-half wrap-midnight covers).
  const { extendedRangeAt, extendedRangeAssignmentsAt } = useMemo(() => {
    const rangeMap = new Map<string, string>();
    const assignMap = new Map<string, Assignment[]>();
    // RAW employee lookup — the projected employeesById from above
    // applies the agency-wide tac overlay, which means a
    // follows-tac officer's homeShiftId reads as their
    // tacHomeShiftId on every date. That breaks the chip helper on
    // pre-transition dates: the "home shift" the helper looks for
    // (B Set, etc.) isn't in the regular-pool, so adjacentShifts
    // can't find the officer's row in rotation and the chip
    // silently drops.
    const rawById = new Map((employees.data ?? []).map((e) => [e.id, e]));
    for (const a of assignments.data ?? []) {
      const emp = rawById.get(a.employeeId);
      if (!emp) continue;
      // Per-date effective home, so an officer flagged
      // followsTacMode lands on Nights for pre-transition cover
      // and B Set for post-transition cover. Skip when that home
      // matches the OT shift — nothing to chip.
      const effectiveHomeId = effectiveHomeShiftFor(agency.data, emp, a.date);
      if (!effectiveHomeId || effectiveHomeId === a.shiftId) continue;
      const note = (a.note ?? "").trim().toUpperCase();
      const cov = note ? shiftCodeByCode.get(note)?.coverageValue : undefined;
      const contributing = !note || cov === undefined || cov > 0;
      if (!contributing) continue;
      const homeShift = (shifts.data ?? []).find((s) => s.id === effectiveHomeId);
      const otShift = (shifts.data ?? []).find((s) => s.id === a.shiftId);
      if (!homeShift || !otShift) continue;
      // Adjacency / chip-date helpers below need the shift family
      // that's actually in rotation on the OT's date — NOT the
      // window-wide union. A page that straddles tac mode would
      // otherwise drag tac shifts into Nights' adjacency pool and
      // make next = "A Set" instead of wrapping to Day, dropping
      // the extended-hours chip silently or placing it on the
      // wrong calendar day for officers whose home shift wraps
      // midnight.
      const visibleShiftList = isTacActiveOn(agency.data, a.date)
        ? (shifts.data ?? []).filter((s) => s.tacRole !== "regular")
        : (shifts.data ?? []).filter((s) => s.tacRole !== "tac");
      // Prefer the explicit hours stored on the OT assignment (from
      // the Fix Coverage window picker or a custom entry). Fall back
      // to the computed half/full range if the assignment predates
      // the window feature.
      let range: string | null;
      if (a.otStartTime && a.otEndTime) {
        range = `${Number(a.otStartTime.split(":")[0])}-${Number(a.otEndTime.split(":")[0])}`;
      } else {
        range = extendedHomeRange(homeShift, otShift, a, visibleShiftList);
      }
      if (!range) continue;
      // Chip placement — decides which calendar date the "6-18" (or
      // "21-9", etc.) label lands on in the officer's home row.
      //
      // Keyed off the coverage ROLE (from the Fix Coverage modal,
      // recorded in overrideReason) instead of the OT hours or the
      // adjacency alone. Role tells us unambiguously what kind of
      // cover this is:
      //
      //   * "full coverage" / "cross-department" / anything else
      //     → chip on a.date. Full covers are always on the OT's
      //       own date; the officer isn't extending a home shift,
      //       they're just working someone else's shift outright.
      //       This is what fixes Colt (V on 8/1, cover A Set 6-18
      //       on 8/2 → chip on 8/2, not 8/1) and Wagner (Night
      //       officer covering Evening 18-06 on 8/2 → chip on 8/2).
      //
      //   * "covering first half" — the officer's home shift is
      //     BEFORE the gap in the rotation. If their home shift
      //     wraps midnight, that home-shift instance started the
      //     day before the OT's calendar day (Night 22-08 8/3
      //     bleeds into a Day 8/4 first-half cover; the chip
      //     belongs on 8/3 where the Night row lives). Otherwise
      //     home is same-day (Evening 14-00 covering first-half
      //     of Night 22-08 8/4 → Evening row is on 8/4 → chip on 8/4).
      //
      //   * "covering second half" — the officer's home shift is
      //     AFTER the gap. If the GAP wraps midnight, the gap's
      //     second half sits on the NEXT calendar day (Day 06-18
      //     8/5 covering second-half of Night 22-08 8/4 → Day row
      //     is on 8/5 → chip on 8/5). Otherwise the officer's
      //     home is same-day as the gap (Evening 14-00 covering
      //     second-half of Day 06-18 8/4 → chip on 8/4).
      const role = (a.overrideReason ?? "").toLowerCase();
      const isFirstHalf = role.includes("first half");
      const isSecondHalf = role.includes("second half");
      let chipDate = a.date;
      if (isFirstHalf && shiftWrapsMidnight(homeShift)) {
        chipDate = shiftYmd(a.date, -1);
      } else if (isSecondHalf && shiftWrapsMidnight(otShift)) {
        chipDate = shiftYmd(a.date, +1);
      }
      const key = `${emp.id}|${chipDate}`;
      rangeMap.set(key, range);
      const bucket = assignMap.get(key);
      if (bucket) bucket.push(a);
      else assignMap.set(key, [a]);
    }
    return {
      extendedRangeAt: rangeMap,
      extendedRangeAssignmentsAt: assignMap,
    };
  }, [assignments.data, employees.data, shiftCodeByCode, shifts.data, agency.data]);

  // ---- Mutations -------------------------------------------------------
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["assignments"] });
    qc.invalidateQueries({ queryKey: ["coverage"] });
    qc.invalidateQueries({ queryKey: ["employees"] });
    qc.invalidateQueries({ queryKey: ["timeoff"] });
  };

  const upsertMut = useMutation({
    mutationFn: async (args: {
      employeeId: string;
      date: string;
      shiftId: string;
      code: string;               // "" means: remove
      existing: Assignment | null;
    }) => {
      if (args.existing) await api.deleteAssignment(args.existing.id);
      if (!args.code && !args.existing) return;
      if (!args.code) return; // removed, nothing more to do
      await api.createAssignment({
        employeeId: args.employeeId,
        date: args.date,
        shiftId: args.shiftId,
        note: args.code,
        overrideReason: args.existing ? "Changed on schedule grid" : undefined,
      });
    },
    onSuccess: invalidate,
  });

  const moveAssignmentMut = useMutation({
    mutationFn: async (args: {
      existingId: string; employeeId: string; shiftId: string; note: string | null;
      toDate: string;
    }) => {
      await api.deleteAssignment(args.existingId);
      await api.createAssignment({
        employeeId: args.employeeId,
        date: args.toDate,
        shiftId: args.shiftId,
        note: args.note ?? undefined,
        overrideReason: "Moved on schedule grid",
      });
    },
    onSuccess: invalidate,
  });

  // Every "move officer between shifts" path runs through this
  // helper so the days-off / IOD materialiser stays in sync with
  // the new state. Without this, the × and + buttons left stale
  // from_pattern XXX rows on the old shift (which then surfaced
  // as orphan chips on the grid) and missed materialising the
  // pattern onto a freshly-added shift. The applyDaysOffPattern
  // helper already handles the three cases internally:
  //   * new target → delete stale on old shift, insert on new
  //   * unchanged target → noop on inserts, only cleans drift
  //   * null target → delete every from_pattern row in window
  const moveOfficerMut = useMutation({
    mutationFn: async (
      { id, patch }: { id: string; patch: Partial<Employee> },
    ) => {
      const saved = await api.updateEmployee(id, patch);
      const isTac = saved.daysOffMode === "tac";
      const target = isTac
        ? (saved.tacHomeShiftId ?? saved.homeShiftId)
        : (saved.homeShiftId ?? saved.tacHomeShiftId);
      const w1 = (isTac ? saved.tacDaysOffWeekdays : saved.daysOffWeekdays) ?? [];
      const w2 = isTac ? saved.tacDaysOffWeekdaysW2 : saved.daysOffWeekdaysW2;
      const sd = saved.daysOffStartDate;
      if (sd) {
        await api.applyDaysOffPattern({
          employeeId: id, week1: w1, week2: w2, startDate: sd,
          homeShiftId: target, silent: true,
        });
      }
      return saved;
    },
    onSuccess: invalidate,
  });

  // Used by the OT row's × button on a Section to remove an
  // overtime-coverage assignment.
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteAssignment(id),
    onSuccess: invalidate,
  });

  // Trims one day out of every approved time-off range that covers
  // (employeeId, date). Used when a supervisor clears or overrides
  // an approved-time-off chip on the schedule grid.
  const removeTimeOffDateMut = useMutation({
    mutationFn: (args: { employeeId: string; date: string }) =>
      api.removeTimeOffDate(args.employeeId, args.date),
    onSuccess: invalidate,
  });

  // ---- Selection + picker state ---------------------------------------
  // A cell key is "employeeId|date". The anchor is the last plain-clicked
  // cell and is used for Shift-click range selection.
  const [selection, setSelection] = useState<Set<string>>(new Set());

  // Deep-link consumer for "→ cell" jumps from the Audit page.
  //   ?focus=<employeeId>|<YYYY-MM-DD>    → select + scroll to cell
  //   ?anchor=<YYYY-MM-DD>                → shift the pay-period
  //                                         window locally so the
  //                                         focused date is in view
  // Both params are consumed once — we clear them off the URL after
  // acting so a page refresh or subsequent navigation doesn't keep
  // re-scrolling the grid.
  useEffect(() => {
    if (!focusParam && !anchorParam) return;
    if (anchorParam && /^\d{4}-\d{2}-\d{2}$/.test(anchorParam)) {
      const [y, m, d] = anchorParam.split("-").map(Number);
      const target = new Date(y, m - 1, d);
      target.setHours(0, 0, 0, 0);
      setLocalAnchor(target);
    }
    if (focusParam && focusParam.includes("|")) {
      setSelection(new Set([focusParam]));
      // Two RAFs so both the anchor change and the roster query
      // have a tick to settle before we look up the cell's DOM node.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(
          `[data-cell-key="${CSS.escape(focusParam)}"]`,
        );
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        // Amber pulse (see @keyframes cellFocusPulse in styles.css)
        // that fades over ~2.5s so the eye can land on the cell
        // before the highlight clears. Restart the animation by
        // removing + reflowing + re-adding the class in case the
        // same cell is focused twice in a row via back/forward.
        el.classList.remove("cell-focus-pulse");
        void el.offsetWidth;
        el.classList.add("cell-focus-pulse");
        setTimeout(() => el.classList.remove("cell-focus-pulse"), 2600);
      }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    next.delete("anchor");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusParam, anchorParam]);

  // Shift sections a supervisor has collapsed on this visit — click
  // the shift's colored header bar to fold everything under it out
  // of sight. In-session only; resets on reload.
  const [collapsedShifts, setCollapsedShifts] = useState<Set<string>>(new Set());
  // Column-hover highlight: tracks the 0..27 dayIndex currently
  // under the cursor, so every cell in the same column lights up
  // across every row. Null when the cursor leaves the table.
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  // Row-hover companion: the employee id for the row under the
  // cursor. Combined with hoveredCol, the two paint a crosshair so
  // a supervisor scanning a long row keeps their place when reading
  // off a date — and reads back which officer lands in any given
  // column. Reset together with hoveredCol on mouseleave.
  const [hoveredEmpId, setHoveredEmpId] = useState<string | null>(null);
  const HOVER_COL_STYLE = { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" };
  function toggleShiftCollapsed(shiftId: string) {
    setCollapsedShifts((prev) => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId); else next.add(shiftId);
      return next;
    });
  }
  const [selAnchor, setSelAnchor] = useState<{ employeeId: string; date: string } | null>(null);
  const [picker, setPicker] = useState<{
    employee: Employee;
    date: string;
    anchor: DOMRect;
  } | null>(null);
  // Inline cell editor — a text input that overlays a single cell
  // with a typeahead dropdown of shift codes. Used for the common
  // case of "click a cell and type", separate from the picker
  // popover which still drives bulk-edits across a multi-cell
  // selection.
  const [inlineEdit, setInlineEdit] = useState<{
    employee: Employee;
    date: string;
    anchor: DOMRect;
    seed: string;
  } | null>(null);
  const [clipboardCode, setClipboardCode] = useState<string | null>(null);
  // Transient error banner (e.g. blocked off-code on a cell where
  // the officer is already covering OT somewhere else that day).
  const [flashError, setFlashError] = useState<string | null>(null);

  // Ordered roster used to compute Shift-click ranges. Must mirror the
  // render order below (we recompute `groups` later). We build a flat
  // list of employeeIds here once the data loads and memoise.
  const employeeOrder = useMemo(() => {
    if (!employees.data || !shifts.data) return [] as string[];
    const active = effectiveEmployees.filter((e) => e.status !== "inactive");
    const shiftOrder = effectiveShifts.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    const byId: Record<string, Employee[]> = {};
    const unassigned: Employee[] = [];
    for (const e of active) {
      if (e.homeShiftId && effectiveShifts.some((s) => s.id === e.homeShiftId)) {
        (byId[e.homeShiftId] ??= []).push(e);
      } else {
        unassigned.push(e);
      }
    }
    const mode = seniorityMode.data ?? "rank_badge";
    const sortRoster = (list: Employee[]) =>
      list.slice().sort((a, b) => compareBySeniority(mode, a, b));
    const out: string[] = [];
    for (const s of shiftOrder) for (const e of sortRoster(byId[s.id] ?? [])) out.push(e.id);
    for (const e of sortRoster(unassigned)) out.push(e.id);
    return out;
  }, [employees.data, shifts.data, effectiveEmployees, effectiveShifts, seniorityMode.data]);

  function keyOf(employeeId: string, date: string) { return `${employeeId}|${date}`; }

  function cellsBetween(
    aEmp: string, aDate: string, bEmp: string, bDate: string,
  ): string[] {
    const empIdx = new Map(employeeOrder.map((id, i) => [id, i]));
    const dayIdx = new Map(days.map((d, i) => [ymd(d), i]));
    const i1 = empIdx.get(aEmp) ?? 0;
    const i2 = empIdx.get(bEmp) ?? 0;
    const j1 = dayIdx.get(aDate) ?? 0;
    const j2 = dayIdx.get(bDate) ?? 0;
    const lo = Math.min(i1, i2), hi = Math.max(i1, i2);
    const loD = Math.min(j1, j2), hiD = Math.max(j1, j2);
    const out: string[] = [];
    for (let i = lo; i <= hi; i++) {
      for (let j = loD; j <= hiD; j++) {
        out.push(keyOf(employeeOrder[i], ymd(days[j])));
      }
    }
    return out;
  }

  function selectCell(e: React.MouseEvent, employee: Employee, date: string) {
    if (!mayEditCells) return;
    const key = keyOf(employee.id, date);

    if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
      // Ctrl / Cmd click: toggle this cell in/out of the selection.
      setSelection((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
      });
      setSelAnchor({ employeeId: employee.id, date });
      return;
    }

    if (e.shiftKey && selAnchor) {
      // Shift click: select the rectangle between anchor and here.
      const range = cellsBetween(selAnchor.employeeId, selAnchor.date, employee.id, date);
      setSelection(new Set(range));
      return;
    }

    // Plain click: if this cell is already part of the current
    // selection, keep the selection intact — otherwise the first
    // click of an eventual double-click would collapse a multi-
    // selection to just this cell before the dblclick fires. If the
    // cell isn't in the selection, replace selection with just it.
    // Either way, set this cell as the anchor for Shift-click ranges.
    if (!selection.has(key)) {
      setSelection(new Set([key]));
    }
    setSelAnchor({ employeeId: employee.id, date });
  }

  function openPicker(e: React.MouseEvent, employee: Employee, date: string) {
    if (!mayEditCells) return;
    // Modifiers on dblclick aren't part of the selection workflow —
    // dblclick just means "open the options for this cell or the
    // active multi-selection". If the clicked cell somehow isn't in
    // the selection, open a single-cell picker on it.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const key = keyOf(employee.id, date);
    if (!selection.has(key)) {
      setSelection(new Set([key]));
      setSelAnchor({ employeeId: employee.id, date });
    }
    setPicker({ employee, date, anchor: rect });
  }

  // Open the inline editor over a cell — used by the page-level
  // type-to-edit shortcut. Looks up the cell's DOM rect by a
  // data-cell-key attribute (see the Section render) instead of
  // taking it from a React event, since the keystroke that
  // triggered this didn't originate from the cell itself.
  //
  // seed is what the editor starts with:
  //   * a printable character → overwrites the current value
  //     (Excel / Google Sheets behaviour)
  //   * "" (Enter / F2)       → preserves the visible chip so the
  //                              supervisor can correct it
  function beginInlineEdit(employee: Employee, date: string, seed: string) {
    if (!mayEditCells) return;
    const key = keyOf(employee.id, date);
    const cellEl = typeof document !== "undefined"
      ? document.querySelector<HTMLElement>(`[data-cell-key="${CSS.escape(key)}"]`)
      : null;
    if (!cellEl) return;
    const rect = cellEl.getBoundingClientRect();
    let resolvedSeed = seed;
    if (seed === "") {
      const existing = assignmentAt.get(key)
        ?? crossAssignmentAt.get(key)
        ?? null;
      resolvedSeed = (existing?.note ?? timeOffAt.get(key) ?? "").trim();
    }
    setInlineEdit({ employee, date, anchor: rect, seed: resolvedSeed });
  }

  function applyCodeToSelection(code: string): { ok: true } | { ok: false; error: string } {
    // Validate + normalize before anything else. Rejects bare
    // numbers ("5"), ambiguous am/pm ("2p"), and typos that don't
    // parse as HH-HH; auto-pads single-digit hours so "9-14"
    // gets stored as "09-14". See normalizeCellCode() below the
    // component for the rule.
    const norm = normalizeCellCode(code);
    if (!norm.ok) {
      // Return the error to the caller so an open picker / inline
      // editor can display it in-form without closing. Callers
      // without a form context (keyboard delete) surface it via
      // setFlashError themselves.
      return { ok: false, error: norm.error };
    }
    const trimmed = norm.value;
    const empById = new Map((employees.data ?? []).map((e) => [e.id, e]));
    const firstShiftId = shifts.data?.[0]?.id ?? "";
    const targets = selection.size > 0
      ? Array.from(selection)
      : (picker ? [keyOf(picker.employee.id, picker.date)] : []);

    // Block off-codes (coverage < 1) on any cell whose officer is
    // already covering an OT somewhere else that same day. An
    // officer on overtime can't simultaneously be Off / Sick / At
    // Court / etc. — the supervisor has to remove the OT chip
    // first (on the covered shift's OT row), then mark them off.
    const newCov = trimmed
      ? shiftCodeByCode.get(trimmed)?.coverageValue
      : undefined;
    const isOffCode = trimmed !== "" && newCov !== undefined && newCov < 1;
    if (isOffCode) {
      const allAssignments = assignments.data ?? [];
      for (const key of targets) {
        const [empId, date] = key.split("|");
        const emp = empById.get(empId);
        if (!emp) continue;
        const onOtElsewhere = allAssignments.some((a) =>
          a.employeeId === emp.id
          && a.date === date
          && a.shiftId !== emp.homeShiftId
          && a.status !== "cancelled"
          && (a.note ?? "").trim().toUpperCase() === "OT",
        );
        if (onOtElsewhere) {
          return {
            ok: false,
            error:
              `${emp.lastName}, ${emp.firstName} is covering overtime on ${date}. `
              + `Remove the OT chip from that shift's coverage row before marking them ${trimmed}.`,
          };
        }
      }
    }

    for (const key of targets) {
      const [empId, date] = key.split("|");
      const emp = empById.get(empId);
      if (!emp) continue;
      const existing = assignmentAt.get(key) ?? null;
      // An "orphan" is an assignment row for this officer + date on
      // a shift OTHER than their current homeShiftId — usually
      // leftover from a previous shift bid (an XXX pattern row that
      // didn't get cleaned up when the officer moved shifts). The
      // cell renders the orphan note as a fallback, so clearing or
      // overwriting the cell needs to delete the orphan too —
      // otherwise the XXX appears to come back on the next render
      // because the underlying row is still in the database.
      const orphan = !existing ? crossAssignmentAt.get(key) ?? null : null;
      const shiftId = emp.homeShiftId ?? firstShiftId;
      if (!shiftId) continue;
      // If the cell is showing an approved time-off overlay (no
      // assignment), the supervisor's grid edit needs to trim that
      // day out of the approved time-off range too — otherwise the
      // Time-off page would still claim the officer is off while
      // the schedule shows them working (or cleared).
      const coveredByTimeOff = !existing && !orphan && timeOffAt.has(key);
      if (coveredByTimeOff) {
        removeTimeOffDateMut.mutate({ employeeId: emp.id, date });
      }
      // Clearing a cell whose only visible chip is a cross-shift OT
      // "extended range" (e.g. Colt Smith's B Set 8/2 showing 6-18
      // because he's covering A Set) has to reach across to that
      // OT row and delete it — otherwise the chip is derived from a
      // row on a different shift, so upsertMut's home-shift-only
      // delete can't touch it and the chip stubbornly persists.
      //
      // We look up by the CHIP's cell key, not (employeeId, date),
      // because a first-half wrap-midnight cover puts the chip on
      // date - 1 while the underlying OT row lives on date. The
      // extendedRangeAssignmentsAt map already keys off chip cell
      // → assignments so we drop whatever ones are backing the
      // visible chip, no matter where the row itself sits.
      if (!trimmed && !existing && !orphan) {
        const otRows = extendedRangeAssignmentsAt.get(key) ?? [];
        for (const a of otRows) deleteMut.mutate(a.id);
      }
      upsertMut.mutate({
        employeeId: emp.id, date, shiftId, code: trimmed,
        existing: existing ?? orphan,
      });
    }
    return { ok: true };
  }

  // Wrapper for callers without their own form (keyboard delete,
  // clipboard paste). Surfaces failures via the schedule-level
  // flash banner since no picker / inline editor is open to
  // catch the error inline.
  function applyCodeFallback(code: string) {
    const r = applyCodeToSelection(code);
    if (!r.ok) setFlashError(r.error);
  }

  // Dismiss the popover on outside click / Escape. Esc also clears selection.
  const popRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function away(ev: MouseEvent) {
      if (popRef.current && !popRef.current.contains(ev.target as Node)) {
        setPicker(null);
      }
    }
    if (picker) document.addEventListener("mousedown", away);
    function key(ev: KeyboardEvent) {
      // Ignore when the user is typing in an input or textarea.
      const t = ev.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;

      if (ev.key === "Escape") {
        setPicker(null);
        setSelection(new Set());
      } else if (ev.key === "Delete" || ev.key === "Backspace") {
        if (selection.size > 0) {
          ev.preventDefault();
          applyCodeFallback("");
        }
      } else if (
        // Type-to-edit: when a single cell is selected and the
        // supervisor starts typing, drop the inline editor over the
        // cell so they can enter a code without ever opening the
        // popover. Enter / F2 reuse the visible value as seed
        // (Excel's "edit in place"); a printable character
        // overwrites it (Excel's "type to replace"). Cmd/Ctrl keys
        // and the picker being open both short-circuit so copy /
        // paste and Enter-inside-popover keep working.
        !picker && !inlineEdit && selection.size === 1 && selAnchor
        && !ev.ctrlKey && !ev.metaKey && !ev.altKey
        && (ev.key === "Enter" || ev.key === "F2"
            || (ev.key.length === 1 && ev.key >= " "))
      ) {
        const emp = employeesById.get(selAnchor.employeeId);
        if (emp) {
          ev.preventDefault();
          const seed = ev.key === "Enter" || ev.key === "F2" ? "" : ev.key;
          beginInlineEdit(emp, selAnchor.date, seed);
        }
      } else if ((ev.ctrlKey || ev.metaKey) && ev.key === "c") {
        // Copy the anchor cell's code (if any) into the in-memory clipboard.
        if (selAnchor) {
          const a = assignmentAt.get(keyOf(selAnchor.employeeId, selAnchor.date));
          setClipboardCode((a?.note ?? "").trim().toUpperCase());
          ev.preventDefault();
        }
      } else if ((ev.ctrlKey || ev.metaKey) && ev.key === "v") {
        if (clipboardCode !== null && selection.size > 0) {
          ev.preventDefault();
          applyCodeFallback(clipboardCode);
        }
      }
    }
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [picker, inlineEdit, selection, selAnchor, clipboardCode,
      assignmentAt, crossAssignmentAt, timeOffAt,
      employees.data, employeesById, shifts.data]);

  // ---- Drag handlers ---------------------------------------------------
  const [dragHint, setDragHint] = useState<{ sectionId?: string; cellKey?: string } | null>(null);

  function dragEmployeeStart(e: DragEvent, emp: Employee) {
    e.dataTransfer.setData(DRAG_EMPLOYEE, emp.id);
    e.dataTransfer.effectAllowed = "move";
  }
  function dragAssignmentStart(e: DragEvent, a: Assignment) {
    e.dataTransfer.setData(
      DRAG_ASSIGNMENT,
      JSON.stringify({
        id: a.id, employeeId: a.employeeId, shiftId: a.shiftId, note: a.note,
        fromDate: a.date,
      }),
    );
    e.dataTransfer.effectAllowed = "move";
  }
  function dragOverSection(e: DragEvent, sectionId: string) {
    if (e.dataTransfer.types.includes(DRAG_EMPLOYEE)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragHint({ sectionId });
    }
  }
  function dropOnSection(e: DragEvent, sectionId: string) {
    const empId = e.dataTransfer.getData(DRAG_EMPLOYEE);
    setDragHint(null);
    if (!empId || !sectionId) return;
    e.preventDefault();
    // Drag-and-drop onto a section header — same write the
    // section's "+ Add officer" button performs. Tac section
    // sets the tac home; regular / exempt sets the standard home.
    const sec = (shifts.data ?? []).find((s) => s.id === sectionId);
    const patch: Partial<Employee> = sec?.tacRole === "tac"
      ? { tacHomeShiftId: sectionId }
      : { homeShiftId: sectionId };
    moveOfficerMut.mutate({ id: empId, patch });
  }
  function dragOverCell(e: DragEvent, cellKey: string) {
    if (e.dataTransfer.types.includes(DRAG_ASSIGNMENT)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragHint({ cellKey });
    }
  }
  function dropOnCell(e: DragEvent, toDate: string) {
    const raw = e.dataTransfer.getData(DRAG_ASSIGNMENT);
    setDragHint(null);
    if (!raw) return;
    e.preventDefault();
    const payload = JSON.parse(raw) as {
      id: string; employeeId: string; shiftId: string; note: string | null;
      fromDate: string;
    };
    if (payload.fromDate === toDate) return;
    moveAssignmentMut.mutate({
      existingId: payload.id,
      employeeId: payload.employeeId,
      shiftId: payload.shiftId,
      note: payload.note,
      toDate,
    });
  }

  // ---- Grouping --------------------------------------------------------
  type Group = { shift: Shift | null; label: string; employees: Employee[] };
  const groups: Group[] = useMemo(() => {
    const active = effectiveEmployees.filter((e) => e.status !== "inactive");
    const shiftOrder = effectiveShifts.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    const byId: Record<string, Employee[]> = {};
    const unassigned: Employee[] = [];
    // Officers appear under every shift they're scheduled to work
    // in the visible window — their home shift plus any temp
    // shift_assignments that overlap. So a rotating officer shows
    // up in both shift sections, with their cells filled only for
    // the days each shift is actually theirs (the cell renderer
    // handles that via shiftLookup).
    for (const e of active) {
      const placements = shiftLookup.shiftsFor(e.id);
      const fitted = [...placements].filter((id) => shiftById[id]);
      if (fitted.length === 0) {
        unassigned.push(e);
        continue;
      }
      for (const sid of fitted) {
        (byId[sid] ??= []).push(e);
      }
    }
    const mode = seniorityMode.data ?? "rank_badge";
    const sortRoster = (list: Employee[]) =>
      list.slice().sort((a, b) => compareBySeniority(mode, a, b));
    const out: Group[] = shiftOrder.map((s) => ({
      shift: s,
      label: s.name.toUpperCase(),
      employees: sortRoster(byId[s.id] ?? []),
    }));
    if (unassigned.length > 0) {
      out.push({ shift: null, label: "UNASSIGNED", employees: sortRoster(unassigned) });
    }
    return out;
  }, [effectiveEmployees, effectiveShifts, shiftById, shiftLookup, seniorityMode.data]);

  function navPayPeriod(deltaDays: number) {
    // Previous / Next / Today update this browser's view only; the
    // shared agency-wide anchor is only moved via the date picker
    // (Owner / L1 only), so a non-supervisor scrolling forward
    // doesn't jerk everyone else's schedule.
    setLocalAnchor(addDays(anchor, deltaDays));
  }

  if (needsRedirect && activeDept) {
    return <Navigate to={`/schedule/${activeDept.id}`} replace />;
  }

  // Mirrors the schedule grid's per-cell render priority (assignment
  // → time-off overlay → cross-shift orphan → IOD overlay → pattern
  // overlay), and accounts for the section gate so a section that
  // doesn't own the (officer, date) shows blank. Used both by the
  // PDF export and any future read-only renderers.
  function pdfCellText(emp: Employee, d: Date, g: ScheduleGroup): string {
    const date = ymd(d);
    const sectionShiftId = g.shift?.id ?? null;
    const effShiftId = shiftLookup.get(emp.id, date);
    const sectionOwnsCell =
      sectionShiftId === null || effShiftId === sectionShiftId;
    if (!sectionOwnsCell) return "";
    const cellKey = `${emp.id}|${date}`;
    const a = assignmentAt.get(cellKey);
    if (a) {
      const note = (a.note ?? "").trim();
      return note;
    }
    const ext = extendedRangeAt.get(cellKey);
    if (ext) return ext;
    const off = timeOffAt.get(cellKey);
    if (off) return off;
    const orphan = crossAssignmentAt.get(cellKey);
    if (orphan) return (orphan.note ?? "").trim();
    if (iodOverlayAt.has(cellKey)) return "IOD";
    if (patternOverlayAt.has(cellKey)) return "XXX";
    return "";
  }
  function pdfCellBg(emp: Employee, d: Date, g: ScheduleGroup): string | null {
    const text = pdfCellText(emp, d, g);
    if (!text) return null;
    const meta = shiftCodes.data?.find((c) => c.code.toUpperCase() === text.toUpperCase());
    if (!meta) return null;
    return meta.color ?? null;
  }

  function handleDownloadPdf() {
    const doc = generateSchedulePdf({
      agencyName: agency.data?.name ?? "Agency",
      departmentName: activeDept?.name ?? null,
      days,
      groups,
      cellText: pdfCellText,
      cellBg: pdfCellBg,
      coverageAt: (shift, d) => {
        const cov = coverageByShiftDate.get(`${shift.id}|${ymd(d)}`);
        return cov ?? null;
      },
      isShiftActiveOn: (shift, d) => isShiftActiveOn(agency.data, shift, ymd(d)),
      codes: shiftCodes.data ?? [],
      tacStart: agency.data?.tacModeActive
        ? (agency.data.tacModeStartDate ?? null)
        : null,
    });
    const range = `${ymd(days[0])}_to_${ymd(days[days.length - 1])}`;
    const deptSlug = (activeDept?.name ?? "Schedule").replace(/\s+/g, "-");
    doc.save(`Schedule_${deptSlug}_${range}.pdf`);
  }

  // Single source of truth for the schedule's table render. Called
  // once from the screen scroll container with the full 28-day
  // `days` array, and twice from the print-only block lower in the
  // JSX — first with days 0-13, then 14-27 — so each printed page
  // shows two readable weeks instead of the whole 4-week grid
  // crammed onto one landscape sheet.
  function renderScheduleTable(daySlice: Date[]) {
    return (
      <table
        className={
          // Beta: drop min-w-max so the 7 day columns + officer
          // column share the iPhone width exactly (no horizontal
          // scroll). Non-beta keeps the original behavior where
          // columns size to their max content and the table
          // overflows horizontally for scrolling.
          betaDesign
            ? "text-xs border-collapse w-full table-fixed schedule-table"
            : "text-xs border-collapse w-full min-w-max schedule-table"
        }
      >
        <thead>
          <tr className="text-slate-500">
            <th
              className={
                (betaDesign
                  // Beta: pin the header cell on mobile too (no md:
                  // gate) and narrow it to w-16 (64px) so the day
                  // columns get the most of the remaining ~330px
                  // budget on an iPhone.
                  ? "sticky left-0 top-0 z-30 bg-slate-50 border-b border-r border-slate-200 px-1 py-2 text-left font-medium w-16"
                  : "md:sticky md:left-0 sticky top-0 z-30 bg-slate-50 border-b border-r border-slate-200 px-3 py-2 text-left font-medium w-40 md:w-56")
              }
            >
              Employee
            </th>
            {daySlice.map((d, i) => (
              <th
                key={d.toISOString()}
                className={cellChrome(
                  d, today, i,
                  "sticky top-0 z-20 bg-slate-50 border-b border-slate-200 px-2 py-2 text-center font-medium" +
                  (i > 0 && d.getDay() === 0 ? " border-l-2 border-l-slate-300" : ""),
                )}
                onMouseEnter={() => setHoveredCol(i)}
                style={i === hoveredCol ? HOVER_COL_STYLE : undefined}
              >
                <div className="uppercase tracking-wide text-[10px] text-slate-500">
                  {format(d, "EEE")}
                </div>
                <div className="text-slate-700">{format(d, "M/d")}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, gi) => (
            <Section
              key={g.shift?.id ?? "_unassigned"}
              group={g}
              days={daySlice}
              today={today}
              first={gi === 0}
              betaDesign={betaDesign}
              isDragHintedSection={dragHint?.sectionId === g.shift?.id}
              dragHintedCellKey={dragHint?.cellKey}
              onSectionDragOver={(e) => g.shift && dragOverSection(e, g.shift.id)}
              onSectionDrop={(e) => g.shift && dropOnSection(e, g.shift.id)}
              assignmentAt={assignmentAt}
              crossAssignmentAt={crossAssignmentAt}
              shiftById={shiftById}
              shiftCodes={shiftCodes.data ?? []}
              onOpenPicker={openPicker}
              onSelectCell={selectCell}
              selection={selection}
              hoveredCol={hoveredCol}
              onHoverCol={setHoveredCol}
              hoveredEmpId={hoveredEmpId}
              onHoverEmp={setHoveredEmpId}
              collapsed={collapsedShifts.has(g.shift?.id ?? "__unassigned__")}
              onToggleCollapsed={() => toggleShiftCollapsed(g.shift?.id ?? "__unassigned__")}
              mayEditRoster={mayEditRoster}
              meEmployeeId={me?.employeeId}
              onDragEmployee={dragEmployeeStart}
              onDragAssignmentStart={dragAssignmentStart}
              onCellDragOver={dragOverCell}
              onCellDrop={dropOnCell}
              onAddEmployeeToShift={(s, employeeId) => {
                const patch: Partial<Employee> = s.tacRole === "tac"
                  ? { tacHomeShiftId: s.id }
                  : { homeShiftId: s.id };
                moveOfficerMut.mutate({ id: employeeId, patch });
              }}
              onRemoveFromShift={(emp) => {
                const sId = g.shift?.id;
                if (!sId) return;
                const patch: Partial<Employee> = {};
                if (emp.homeShiftId === sId) patch.homeShiftId = null;
                if (emp.tacHomeShiftId === sId) patch.tacHomeShiftId = null;
                moveOfficerMut.mutate({ id: emp.id, patch });
              }}
              addableEmployees={(employees.data ?? []).filter(
                (e) => e.status !== "inactive" && e.homeShiftId !== g.shift?.id,
              )}
              coverageByShiftDate={coverageByShiftDate}
              otByShiftDate={otByShiftDate}
              extendedRangeAt={extendedRangeAt}
              timeOffAt={timeOffAt}
              iodOverlayAt={iodOverlayAt}
              patternOverlayAt={patternOverlayAt}
              employeesById={employeesById}
              onFixCoverage={mayEditRoster
                ? (shiftId, date) => setFixCoverage({ shiftId, date })
                : undefined}
              onDeleteOt={mayEditRoster
                ? (a) => deleteMut.mutate(a.id)
                : undefined}
              shiftLookup={shiftLookup}
              isShiftActiveOnDate={(date) =>
                !g.shift || isShiftActiveOn(agency.data, g.shift, date)}
            />
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {flashError && (
        <div className="mx-6 mt-6 mb-3 flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 shrink-0">
          <span className="flex-1">{flashError}</span>
          <button
            type="button"
            onClick={() => setFlashError(null)}
            className="text-red-400 hover:text-red-700 font-semibold leading-none"
            title="Dismiss"
          >×</button>
        </div>
      )}
      {/* Mobile: cancel <main>'s pt-12 (which reserves space for
        * the fixed hamburger) with -mt-12 so the controls row
        * shares the vertical band with the hamburger. flex-col-
        * reverse puts the controls row first and the title row
        * below. Desktop: original two-column header, no offset
        * — the hamburger isn't visible on md+ anyway.
        *
        * Beta drops the -mt-12 so the title row sits below the
        * hamburger / bell instead of overlapping them. The beta
        * header is also the only nav now (no Previous/Today/Next
        * buttons to share the band with). */}
      <header
        className={
          "flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-1 md:gap-0 mb-1 md:mb-4 px-4 md:px-6 pt-3 md:pt-6 shrink-0 md:mt-0 "
          + (betaDesign ? "" : "-mt-12")
        }
      >
        <div className="min-w-0">
          {/* Mobile: one tiny line, all info inline. */}
          <div className="md:hidden flex items-baseline gap-1.5 truncate text-xs leading-tight">
            <span className="font-semibold text-slate-800">
              {activeDept?.name ?? "Schedule"}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500 truncate">
              {format(days[0], "MMM d")} – {format(days[days.length - 1], "MMM d, yyyy")}
            </span>
            {selection.size > 1 && (
              <span className="ml-1 inline-flex items-center text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1 py-0 print:hidden">
                {selection.size}
              </span>
            )}
          </div>
          {/* Desktop: original h1 + subtitle layout. */}
          <h1 className="hidden md:block text-xl font-semibold text-slate-900 truncate">
            {activeDept?.name ?? "Schedule"}
          </h1>
          <p className="hidden md:block text-sm text-slate-500">
            {format(days[0], "MMM d")} – {format(days[days.length - 1], "MMM d, yyyy")}
            {!betaDesign && " (4 weeks)"}
            <span className="ml-3 text-slate-400 print:hidden">
              · click to pick a code · Shift-click range · Ctrl-click toggle
              · Ctrl+C / Ctrl+V / Del · Esc clears
            </span>
            {selection.size > 1 && (
              <span className="ml-3 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 print:hidden">
                {selection.size} selected
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap md:flex-nowrap pl-12 pr-12 md:pl-0 md:pr-0 print:hidden">
          {/* Beta hides the anchor/Previous/Today/Next chrome — the
            * window is pinned to "around today" so there's nothing
            * to navigate. */}
          {!betaDesign && (
            <>
              <label className="text-xs text-slate-500 flex items-center gap-1.5">
                <span className="hidden md:inline">Start</span>
                <input
                  type="date"
                  value={ymd(days[0])}
                  disabled={!maySetAnchor || setAnchorMut.isPending}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    const [y, m, dd] = v.split("-").map(Number);
                    const next = new Date(y, (m ?? 1) - 1, dd ?? 1);
                    next.setHours(0, 0, 0, 0);
                    setSharedAnchor(next);
                  }}
                  className="text-xs px-2 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                  title={maySetAnchor
                    ? "Change the shared pay period anchor for every user"
                    : "Only an Owner or Level 1 account can change the shared pay period anchor."}
                />
              </label>
              <Button
                variant="secondary" size="sm"
                onClick={() => navPayPeriod(-28)}
                title="Show the previous 4 weeks (local to this browser)"
              >
                <span className="md:hidden">‹</span>
                <span className="hidden md:inline">← Previous</span>
              </Button>
              <Button
                variant="secondary" size="sm"
                title="Jump to the 4-week page containing today"
                onClick={() => {
                  const t = new Date(); t.setHours(0, 0, 0, 0);
                  const msPerDay = 86_400_000;
                  const diff = Math.floor((t.getTime() - anchor.getTime()) / msPerDay);
                  const pages = Math.floor(diff / 28);
                  if (pages !== 0) setLocalAnchor(addDays(anchor, pages * 28));
                  else setLocalAnchor(anchor);
                }}
              >
                Today
              </Button>
              <Button
                variant="secondary" size="sm"
                onClick={() => navPayPeriod(28)}
                title="Show the next 4 weeks (local to this browser)"
              >
                <span className="md:hidden">›</span>
                <span className="hidden md:inline">Next →</span>
              </Button>
            </>
          )}
          <Button
            variant="secondary" size="sm"
            onClick={() => window.print()}
            title="Open the browser print dialog — four weeks on one landscape page."
            className="hidden md:inline-flex"
          >
            <span className="md:hidden">⎙</span>
            <span className="hidden md:inline">Print</span>
          </Button>
          <Button
            variant="secondary" size="sm"
            onClick={handleDownloadPdf}
            title="Download a typeset PDF — same two-week split per page, deterministic layout."
            className="hidden md:inline-flex"
          >
            <span className="md:hidden">PDF</span>
            <span className="hidden md:inline">Download PDF</span>
          </Button>
        </div>
      </header>

      {/* On-call rotations — today's primary/secondary across every
        * roster, one compact line. Lives outside the schedule's
        * scroll container; the full 4-week roster grid lives on
        * the dedicated On-Call page. Renders nothing when the
        * "oncall" flag is off. */}
      <OnCallTodayStrip />

      {(employees.data ?? []).length === 0 && (
        <div className="mb-4 mx-6 p-4 bg-sky-50 border border-sky-200 rounded-md text-sm text-sky-900 shrink-0">
          No employees yet. Add them on <strong>Employees</strong>, or load
          25 placeholders from <strong>Settings → Sample data</strong>.
        </div>
      )}

      <div
        // The page is a flex column pinned to main's height; this
        // wrapper takes all remaining vertical room (flex-1 +
        // min-h-0) and owns both overflow axes. That makes it the
        // nearest scrolling ancestor for the sticky thead's
        // `top-0` so the date header actually sticks, and there's
        // no leftover dead space at the bottom of the viewport
        // for a stray scrollbar to park in.
        //
        // Mobile gets `snap-y snap-proximity` so vertical scrolling
        // through a long schedule eases onto each shift header
        // instead of free-wheeling past them. Proximity (not
        // mandatory) so the snap only kicks in when the user is
        // already near a header — fast flicks still glide
        // naturally. Desktop opts out (`md:snap-none`) because
        // mouse-wheel users find any snap annoying.
        //
        // For print we DON'T split the table — the browser print
        // path renders all four weeks on one landscape page
        // (zoom 0.58 in styles.css) the way it used to. The
        // typeset PDF download (Download PDF button) handles the
        // legibility case by splitting into two pages at typeset
        // resolution instead of relying on the browser's print
        // engine.
        className="bg-white border-t border-slate-200 overflow-auto flex-1 min-h-0 snap-y snap-proximity md:snap-none"
        onMouseLeave={() => { setHoveredCol(null); setHoveredEmpId(null); }}
        // Beta swipe-to-paginate. Records the initial X on
        // touchstart and on touchend compares the delta — if it's
        // beyond a threshold AND mostly horizontal, advances /
        // reverses the week offset. The 50px / horizontal-dominance
        // checks avoid hijacking vertical scrolls and tiny taps.
        onTouchStart={(e) => {
          if (!betaDesign) return;
          const t = e.touches[0];
          (e.currentTarget as unknown as { __swipe?: { x: number; y: number } })
            .__swipe = { x: t.clientX, y: t.clientY };
        }}
        onTouchEnd={(e) => {
          if (!betaDesign) return;
          const start = (e.currentTarget as unknown as { __swipe?: { x: number; y: number } }).__swipe;
          if (!start) return;
          const t = e.changedTouches[0];
          const dx = t.clientX - start.x;
          const dy = t.clientY - start.y;
          (e.currentTarget as unknown as { __swipe?: unknown }).__swipe = undefined;
          if (Math.abs(dx) < 50) return;
          if (Math.abs(dx) < Math.abs(dy) * 1.5) return; // vertical-dominant, ignore
          setBetaWeekOffset((o) => o + (dx < 0 ? 1 : -1));
        }}
      >
        {renderScheduleTable(days)}
      </div>

      {picker && (() => {
        // The popover treats either the home-shift assignment, a
        // visible orphan (cross-shift leftover row), or the cross-
        // shift OT row driving an extended-range chip as the
        // "existing" chip — that way "Remove code" works on the row
        // the supervisor actually sees, even when that row lives on
        // a shift the officer is no longer rostered to (orphan) or a
        // neighboring shift they're covering (OT).
        //
        // For the OT case we consult extendedRangeAssignmentsAt,
        // keyed on the CHIP's cell — the underlying OT row can be
        // on a different date (first-half wrap covers) so a same-
        // (employee, date) scan would miss it and hide "Remove code".
        const pkey = `${picker.employee.id}|${picker.date}`;
        const pOtRow = (!assignmentAt.get(pkey) && !crossAssignmentAt.get(pkey))
          ? extendedRangeAssignmentsAt.get(pkey)?.[0] ?? null
          : null;
        const pExisting = assignmentAt.get(pkey)
          ?? crossAssignmentAt.get(pkey)
          ?? pOtRow
          ?? null;
        return (
          <CodePickerPopover
            ref={popRef}
            anchor={picker.anchor}
            employee={picker.employee}
            date={picker.date}
            codes={shiftCodes.data ?? []}
            existing={pExisting}
            timeOffCode={pExisting ? null : timeOffAt.get(pkey) ?? null}
            selectionSize={selection.size}
            onPick={applyCodeToSelection}
            onClear={() => applyCodeToSelection("")}
            onClose={() => setPicker(null)}
            onViewHistory={mayViewAudit ? () => {
              // Route to the Audit page pre-filtered to this cell.
              // Uses the officer's current effective home shift when
              // no assignment exists in the cell — that's the shift
              // the cell is displaying under, so it's the right
              // audit scope for "what happened here".
              const empHome = picker.employee.homeShiftId ?? "";
              const targetShift = pExisting?.shiftId ?? empHome;
              const params = new URLSearchParams({
                employee: picker.employee.id,
                date: picker.date,
                shift: targetShift,
              });
              setPicker(null);
              nav(`/audit?${params.toString()}`);
            } : undefined}
          />
        );
      })()}

      {inlineEdit && (
        <InlineCellEditor
          anchor={inlineEdit.anchor}
          codes={shiftCodes.data ?? []}
          seed={inlineEdit.seed}
          onCommit={applyCodeToSelection}
          onClear={() => applyCodeToSelection("")}
          onClose={() => setInlineEdit(null)}
        />
      )}

      {fixCoverage && (() => {
        const sh = (shifts.data ?? []).find((s) => s.id === fixCoverage.shiftId);
        if (!sh) return null;
        return (
          <FixCoverageModal
            shift={sh}
            date={fixCoverage.date}
            onClose={() => setFixCoverage(null)}
          />
        );
      })()}
    </div>
  );
}

// ---- Section subtree ----------------------------------------------------

interface SectionProps {
  group: { shift: Shift | null; label: string; employees: Employee[] };
  days: Date[];
  today: Date;
  first: boolean;
  isDragHintedSection: boolean;
  dragHintedCellKey: string | undefined;
  onSectionDragOver: (e: DragEvent<HTMLElement>) => void;
  onSectionDrop: (e: DragEvent<HTMLElement>) => void;
  assignmentAt: Map<string, Assignment>;
  // Cross-shift assignments (OT or orphan off-codes) keyed by
  // employee|date. Used by the home cell render to show the
  // extended-hours chip or fall back to an orphan code when the
  // employee has no home-shift assignment for that day.
  crossAssignmentAt: Map<string, Assignment>;
  shiftById: Record<string, Shift>;
  shiftCodes: ShiftCode[];
  onOpenPicker: (e: React.MouseEvent, emp: Employee, date: string) => void;
  onSelectCell: (e: React.MouseEvent, emp: Employee, date: string) => void;
  selection: Set<string>;
  mayEditRoster: boolean;
  meEmployeeId: string | null | undefined;
  onDragEmployee: (e: DragEvent<HTMLDivElement>, emp: Employee) => void;
  onDragAssignmentStart: (e: DragEvent<HTMLDivElement>, a: Assignment) => void;
  onCellDragOver: (e: DragEvent<HTMLTableCellElement>, cellKey: string) => void;
  onCellDrop: (e: DragEvent<HTMLTableCellElement>, toDate: string) => void;
  onAddEmployeeToShift: (shift: Shift, employeeId: string) => void;
  onRemoveFromShift: (employee: Employee) => void;
  addableEmployees: Employee[];
  coverageByShiftDate: Map<string, {
    assigned: number; minStaffing: number;
    oicAssigned: number; oicRequired: number;
    overridden: boolean;
  }>;
  // Cross-shift OT assignments attached to this Section's shift,
  // indexed by `${shiftId}|${date}`. Each entry becomes an OT row.
  otByShiftDate: Map<string, Assignment[]>;
  // For each (employee, date) where the employee is doing OT on a
  // neighboring shift, the extended worked-hours label to display
  // in their home cell ("21-11" etc).
  extendedRangeAt: Map<string, string>;
  timeOffAt: Map<string, string>;
  // (employeeId|date) → true when an IOD period covers this cell.
  // Used as a last-resort overlay for unassigned officers whose
  // row has no shift to write IOD assignment rows against.
  iodOverlayAt: Map<string, true>;
  // (employeeId|date) → true when the unassigned officer's
  // days-off pattern says they're off this weekday. Materialiser
  // can't write XXX rows without a shift, so the grid falls back
  // to this overlay.
  patternOverlayAt: Map<string, true>;
  employeesById: Map<string, Employee>;
  // When provided, red coverage cells become clickable and open the
  // fix-coverage modal. Undefined for non-supervisors.
  onFixCoverage?: (shiftId: string, date: string) => void;
  // Undefined for non-supervisors; disables the × on OT rows.
  onDeleteOt?: (a: Assignment) => void;
  // When true, hide every row under the shift header (date row,
  // officer rows, coverage row, OIC row, OT rows). Click the
  // header to toggle. Undefined → header not clickable (unassigned
  // rosters don't collapse).
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  // Shared hover highlight — column index under the cursor across
  // the whole schedule. Cells at this index paint a faint blue so
  // the full column reads as one unit while the user scans a row.
  hoveredCol: number | null;
  onHoverCol: (i: number | null) => void;
  // Companion to hoveredCol: the employee id for the row under
  // the cursor. Officer rows tint the same faint blue across all
  // their day cells so the row + column form a crosshair.
  hoveredEmpId: string | null;
  onHoverEmp: (id: string | null) => void;
  // Per-(officer,date) effective shift lookup. Used to grey out
  // cells when the officer is on a different shift that day
  // (rotating shift assignment in effect). Cells where the
  // officer's effective shift matches this section's shift render
  // normally; cells where it doesn't render faded + non-
  // interactive so it's clear they're "borrowed" from another
  // shift's view of the same officer.
  shiftLookup: { get(employeeId: string, date: string): string | null };
  // Per-date "is this section's shift in rotation today" gate.
  // Returns false on tac-transition pages for the regular shifts
  // post-transition and the tac shifts pre-transition. Those days
  // render as N/A rather than 0/3 short on the coverage row + OIC
  // row, and the officer cells dim out.
  isShiftActiveOnDate: (date: string) => boolean;
  // iOS beta-design mode: condense officer-row labels to initials.
  // Owner-only and iOS-only — see useBetaDesign in lib/platform.ts.
  betaDesign: boolean;
}

function Section(p: SectionProps) {
  const { group, days, first } = p;
  const sectionColor = group.shift?.color ?? "#64748b";
  // The bar uses the shift's full neon hue as the background and
  // picks a white-or-black text color via luminance so bright
  // yellow / lime keep black text and the deeper hues flip to
  // white. A soft inner divider colour stays muted (slate-500
  // on yellow, white/70 on blue) so the "06:00–18:00 · 8
  // officers" metadata reads as secondary text.
  const headerText = readableTextOn(sectionColor);
  const metaText = headerText === "#ffffff"
    ? "rgba(255,255,255,0.78)"
    : "rgba(15,23,42,0.65)";
  const rowCount = group.employees.length;

  return (
    <>
      {!first && (
        <tr>
          <td colSpan={days.length + 1} className="h-4 bg-slate-100 border-0" />
        </tr>
      )}
      <tr
        onDragOver={p.onSectionDragOver}
        onDrop={p.onSectionDrop}
        // snap-start + scroll-margin-top so vertical scrolling on
        // mobile eases each shift header into view just below the
        // sticky date row, instead of letting it slip past. The
        // sticky date row is ~48px tall (two-line "EEE / M/d" plus
        // py-2), so scroll-mt-14 (56px) gives the shift bar a few
        // pixels of clearance below the date strip — without the
        // extra cushion the bar's top edge ends up hidden behind
        // the date strip and only the text inside the bar shows.
        // Desktop ignores snap (set via `md:snap-none` on the
        // scroll container) so mouse-wheel users keep their free
        // scroll.
        className={
          "text-[11px] uppercase tracking-wider font-semibold snap-start scroll-mt-14 "
          + (p.isDragHintedSection ? "outline outline-2 outline-sky-400 outline-offset-[-2px]" : "")
        }
      >
        <th
          colSpan={days.length + 1}
          className={
            "text-left px-3 py-1 border-y border-slate-200 "
            + (p.onToggleCollapsed ? "cursor-pointer select-none" : "")
          }
          style={{
            color: headerText,
            backgroundColor: sectionColor,
          }}
          onClick={p.onToggleCollapsed}
          title={p.onToggleCollapsed
            ? (p.collapsed ? "Click to expand this shift" : "Click to collapse this shift")
            : undefined}
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-block w-3 text-center transition-transform"
              style={{ transform: p.collapsed ? "rotate(-90deg)" : undefined }}
            >▾</span>
            <span>●</span>
            <span>{group.label}</span>
            {group.shift && (
              <span className="font-normal normal-case" style={{ color: metaText }}>
                · {group.shift.startTime}–{shiftEndTime(group.shift.startTime, group.shift.durationMin)} · {rowCount} officer{rowCount === 1 ? "" : "s"}
                {p.collapsed && <span className="ml-2 italic" style={{ color: metaText, opacity: 0.8 }}>collapsed</span>}
              </span>
            )}
          </div>
        </th>
      </tr>
      {p.collapsed ? null : <SectionBody p={p} />}
    </>
  );
}

// The body of a shift section — date row, per-officer rows, add
// officer button, Coverage row, OIC row, OT rows. Split out so the
// Section header can short-circuit rendering when collapsed.
function SectionBody({ p }: { p: SectionProps }) {
  const { group, days, today, assignmentAt, shiftCodes } = p;
  const codeMap = useMemo(
    () => new Map(shiftCodes.map((c) => [c.code.toUpperCase(), c])),
    [shiftCodes],
  );
  return (
    <>
      {group.employees.map((emp) => {
        const isMe = !!p.meEmployeeId && emp.id === p.meEmployeeId;
        const isHoveredRow = p.hoveredEmpId === emp.id;
        return (
        <tr
          key={emp.id}
          className={
            "border-b border-slate-100" +
            (isMe ? " bg-sky-50/60 ring-1 ring-sky-300 ring-inset" : "")
          }
          onMouseEnter={() => p.onHoverEmp(emp.id)}
          title={isMe ? "This is you" : undefined}>
          <td
            className={
              (p.betaDesign
                // Beta: always-sticky 64px column so the full week
                // fits an iPhone with no horizontal scroll. Tight
                // padding + overflow-hidden truncate gracefully
                // when initials + a long radio number would
                // otherwise wrap.
                ? "sticky left-0 z-10 border-r border-slate-200 px-1 py-0.5 whitespace-nowrap w-16 max-w-16 overflow-hidden "
                : "md:sticky md:left-0 z-10 border-r border-slate-200 px-3 py-0.5 whitespace-nowrap ") +
              (isMe ? "bg-sky-50" : "bg-white")
            }
            style={isHoveredRow
              ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
              : undefined}
          >
            <div className="flex items-center gap-2">
              {/* × remove button — supervisor-only on shifts that
                * accept a roster. Hidden in beta because the column
                * is too narrow for affordances and the action lives
                * elsewhere in the iOS reskin (TBD — for now you can
                * still drag to reassign). */}
              {!p.betaDesign && group.shift && p.mayEditRoster && (
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    p.onRemoveFromShift(emp);
                  }}
                  title={`Remove ${emp.lastName} from this shift`}
                  className="shrink-0 text-slate-400 hover:text-white hover:bg-red-500 rounded w-4 h-4 flex items-center justify-center text-xs leading-none transition border border-slate-300 hover:border-red-500 print:hidden"
                >
                  ×
                </button>
              )}
              <div
                draggable
                onDragStart={(e) => p.onDragEmployee(e, emp)}
                className="cursor-grab active:cursor-grabbing select-none flex-1 min-w-0"
                title="Drag to another shift to reassign home shift"
              >
              <div className="flex items-center gap-1.5">
                <span
                  className={
                    // Beta: OIC officers stand out as bold royal-blue
                    // (no separate OIC badge — the color IS the badge).
                    // Non-OIC stay in the default name treatment.
                    p.betaDesign && emp.isOic
                      ? "font-bold text-blue-700"
                      : "font-medium text-slate-900"
                  }
                >
                  {p.betaDesign
                    ? `${emp.firstName?.[0] ?? ""}.${emp.lastName?.[0] ?? ""}.`
                    : <>{emp.rank ? `${emp.rank}. ` : ""}{emp.lastName}, {emp.firstName}</>}
                </span>
                {/* OIC badge — hidden in beta since the name color
                  * conveys the same information without taking a
                  * second visual slot. */}
                {!p.betaDesign && emp.isOic && (
                  <span className="inline-block px-1 py-0 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200">
                    OIC
                  </span>
                )}
                {(emp.radioNumber ?? emp.badgeNumber) && (
                  <span className="text-[10px] text-slate-400 font-mono">
                    {/* Prefer radio call sign over badge — supervisors
                      * address officers by radio number on the air, so it
                      * reads faster on the schedule than the badge.
                      * Falls back to badge when no radio is on file. */}
                    #{emp.radioNumber ?? emp.badgeNumber}
                  </span>
                )}
              </div>
              </div>
            </div>
          </td>
          {days.map((d, i) => {
            const date = ymd(d);
            const cellKey = `${emp.id}|${date}`;
            const a = assignmentAt.get(cellKey);
            const homeNote = a?.note?.trim().toUpperCase() ?? "";
            const homeCode = homeNote ? codeMap.get(homeNote) : undefined;
            const isDropHinted = p.dragHintedCellKey === cellKey;
            const isSelected = p.selection.has(cellKey);
            // When the officer is rendered under multiple shift
            // sections (a temporary shift_assignments row in
            // effect), grey out the days they're NOT on this
            // section's shift so the supervisor can see at a
            // glance which section "owns" them that day. The
            // unassigned section has shift = null and shows all
            // their cells (they're not under any shift to begin
            // with).
            const effShiftId = p.shiftLookup.get(emp.id, date);
            const sectionOwnsCell = p.group.shift === null
              || effShiftId === p.group.shift.id;
            // Rendering priority for the home cell:
            //  1. Cross-shift overtime on this date — show the
            //     extended-hours chip (e.g. "22-11"). Takes priority
            //     over a home-shift off-code because the officer
            //     *just picked up OT*; hiding it behind an XXX /
            //     vacation chip would make the OT assignment
            //     invisible. Removing the OT restores the off-code.
            //  2. A home-shift off-code (XXX, S, V, …) — show it.
            //  3. A home-shift custom/working note that isn't an
            //     off-code — show it as free-text.
            //  4. Fallback orphan (cross-shift off-code left behind
            //     after a home-shift change) — show its note so it
            //     doesn't silently disappear.
            //  5. Empty.
            const isOffCode = !!homeCode && homeCode.coverageValue < 1;
            const extRange = p.extendedRangeAt.get(cellKey);
            // Approved time-off overlay. Only kicks in when the
            // cell is otherwise blank (no assignment) — a manual
            // edit on that day takes precedence so the supervisor
            // can stamp a more specific code without re-processing
            // the time-off request.
            const timeOffCode = !a ? p.timeOffAt.get(cellKey) : undefined;
            const timeOffMeta = timeOffCode ? codeMap.get(timeOffCode) : undefined;
            const orphan = !a && !extRange && !timeOffCode
              ? p.crossAssignmentAt.get(cellKey)
              : undefined;
            const orphanNote = orphan?.note?.trim().toUpperCase() ?? "";
            const orphanCode = orphanNote ? codeMap.get(orphanNote) : undefined;
            // Unassigned-officer IOD overlay. Only kicks in when
            // nothing else fills the cell — the actual IOD
            // assignment rows for shift-assigned officers are
            // picked up via `a` above.
            const iodOverlay = !a && !extRange && !timeOffCode && !orphan
              && p.iodOverlayAt.has(cellKey);
            const iodOverlayMeta = iodOverlay ? codeMap.get("IOD") : undefined;
            // Days-off pattern overlay (XXX) for unassigned
            // officers. Lower priority than the IOD overlay above
            // so an IOD range visually trumps a regular off-day.
            const patternOverlay = !a && !extRange && !timeOffCode && !orphan && !iodOverlay
              && p.patternOverlayAt.has(cellKey);
            const patternOverlayMeta = patternOverlay ? codeMap.get("XXX") : undefined;
            return (
              <td
                key={date}
                data-cell-key={cellKey}
                onClick={sectionOwnsCell ? (e) => p.onSelectCell(e, emp, date) : undefined}
                onDoubleClick={sectionOwnsCell ? (e) => p.onOpenPicker(e, emp, date) : undefined}
                onDragOver={sectionOwnsCell ? (e) => p.onCellDragOver(e, cellKey) : undefined}
                onDrop={sectionOwnsCell ? (e) => p.onCellDrop(e, date) : undefined}
                onMouseEnter={() => p.onHoverCol(i)}
                className={cellChrome(
                  d, today, i,
                  "p-0 h-6 min-w-[52px] text-center align-middle border border-slate-200 relative" +
                  (sectionOwnsCell ? " cursor-pointer" : " cursor-default bg-slate-100/70") +
                  (i > 0 && d.getDay() === 0 ? " border-l-2 border-l-slate-300" : "") +
                  (isDropHinted ? " outline outline-2 outline-sky-400" : "") +
                  (isSelected ? " outline outline-2 outline-sky-500 outline-offset-[-2px] z-10" : ""),
                )}
                style={i === p.hoveredCol || isHoveredRow
                  ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                  : undefined}
                title={!sectionOwnsCell
                  ? "On a different shift this day"
                  : (extRange ? `Working overtime — ${extRange}` : undefined)}
              >
                {!sectionOwnsCell ? null : extRange ? (
                  <div className="w-full h-full flex items-center justify-center bg-amber-100 text-amber-800 text-[11px] font-semibold leading-none select-none">
                    {extRange}
                  </div>
                ) : isOffCode ? (
                  <CellChip
                    note={homeNote}
                    codeMeta={homeCode}
                    draggable={!!a}
                    onDragStart={a ? (e) => p.onDragAssignmentStart(e, a) : undefined}
                  />
                ) : homeNote ? (
                  <CellChip
                    note={homeNote}
                    codeMeta={homeCode}
                    draggable={!!a}
                    onDragStart={a ? (e) => p.onDragAssignmentStart(e, a) : undefined}
                  />
                ) : timeOffCode ? (
                  <CellChip
                    note={timeOffCode}
                    codeMeta={timeOffMeta}
                    draggable={false}
                  />
                ) : orphan ? (
                  <CellChip
                    note={orphanNote}
                    codeMeta={orphanCode}
                    draggable={false}
                  />
                ) : iodOverlay ? (
                  <CellChip
                    note="IOD"
                    codeMeta={iodOverlayMeta}
                    draggable={false}
                  />
                ) : patternOverlay ? (
                  <CellChip
                    note="XXX"
                    codeMeta={patternOverlayMeta}
                    draggable={false}
                  />
                ) : (
                  <CellChip note="" codeMeta={undefined} draggable={false} />
                )}
              </td>
            );
          })}
        </tr>
        );
      })}
      {/* + Add officer row — hidden in beta since the iOS reskin
        * doesn't have a home for the inline picker yet (TBD; the
        * picker dropdown also assumes a desktop-sized popover). */}
      {group.shift && p.mayEditRoster && !p.betaDesign && (
        <tr className="border-b border-slate-100 print:hidden">
          <td colSpan={days.length + 1} className="md:sticky md:left-0 z-10 bg-white px-3 py-0.5">
            <AddOfficerButton
              shift={group.shift}
              addable={p.addableEmployees}
              onPick={(emp) => p.onAddEmployeeToShift(group.shift!, emp.id)}
            />
          </td>
        </tr>
      )}
      {group.shift && (() => {
        // One OT row per "group slot" so every coverage event gets
        // its own cell. Row count = max number of coverage groups on
        // any single day across the visible range. On days with
        // fewer groups than the row count the extra rows render an
        // empty cell. Rendered above Coverage / OIC so the
        // coverage-check rows sit at the section's bottom where the
        // supervisor's eye naturally lands.
        const shiftId = group.shift.id;
        const groupsByDay = days.map((d) =>
          groupOTByCoverage(p.otByShiftDate.get(`${shiftId}|${ymd(d)}`) ?? []),
        );
        const rowCount = groupsByDay.reduce((m, g) => Math.max(m, g.length), 0);
        if (rowCount === 0) return null;

        return (
          <>
            {Array.from({ length: rowCount }, (_, rowIdx) => (
              <tr key={`ot-${rowIdx}`} className="border-b border-slate-100 bg-amber-50/60">
                <td
                  className={
                    (p.betaDesign
                      ? "sticky left-0 z-10 px-1 w-16 max-w-16 overflow-hidden "
                      : "md:sticky md:left-0 z-10 px-3 ") +
                    "bg-amber-50/60 border-r border-slate-200 py-0.5 whitespace-nowrap"
                  }
                >
                  {rowIdx === 0 && (
                    <>
                      <span className="text-[10px] font-semibold text-amber-700 mr-1.5">OT</span>
                      <span className="text-slate-500 text-[11px]">Coverage</span>
                    </>
                  )}
                </td>
                {days.map((d, i) => {
                  const date = ymd(d);
                  const g = groupsByDay[i][rowIdx];
                  // OT chips don't render on dates the shift isn't
                  // in rotation — same gate the Coverage / OIC rows
                  // already use. Prevents an old OT entry on a
                  // tac-transition straddling page from leaking
                  // into the post-transition columns where the
                  // shift is N/A.
                  const shiftActive = p.isShiftActiveOnDate(date);
                  return (
                    <td
                      key={date}
                      onMouseEnter={() => p.onHoverCol(i)}
                      style={i === p.hoveredCol
                        ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                        : undefined}
                      className={cellChrome(
                        d, today, i,
                        "p-0 h-6 text-center align-middle border border-slate-200 relative" +
                        (i > 0 && d.getDay() === 0 ? " border-l-2 border-l-slate-300" : "") +
                        (shiftActive ? "" : " bg-slate-100"),
                      )}
                    >
                      {shiftActive && g && (
                        <OTGroupChip
                          rows={g}
                          employeesById={p.employeesById}
                          onDeleteOt={p.onDeleteOt}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </>
        );
      })()}
      {group.shift && (
        <tr className="border-t border-slate-200">
          <td
            className={
              (p.betaDesign
                ? "sticky left-0 z-10 px-1 w-16 max-w-16 overflow-hidden "
                : "md:sticky md:left-0 z-10 px-3 ") +
              "bg-slate-50 border-r border-slate-200 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500"
            }
          >
            Coverage
          </td>
          {days.map((d, i) => {
            const date = ymd(d);
            const cov = p.coverageByShiftDate.get(`${group.shift!.id}|${date}`);
            const assigned = cov?.assigned ?? 0;
            const required = cov?.minStaffing ?? group.shift!.minStaffing;
            const oicAssigned = cov?.oicAssigned ?? 0;
            const oicRequired = cov?.oicRequired ?? 1;
            // Shift not in rotation on this date (transition page,
            // regular shift on a tac date, or vice versa). Render
            // N/A so the cell doesn't blare red 0/3 short for a
            // shift that isn't actually scheduled to run that day.
            const shiftActive = p.isShiftActiveOnDate(date);
            // Supervisor override flips "short" off regardless of
            // the underlying bucket math. The number still renders
            // honestly so the supervisor sees the raw count.
            const overridden = cov?.overridden ?? false;
            const totalShort = shiftActive && !overridden && assigned < required;
            const oicShort = shiftActive && !overridden && oicAssigned < oicRequired;
            const short = totalShort || oicShort;
            const reasons: string[] = [];
            if (totalShort) reasons.push(`${fmtNum(assigned)} of ${required} working`);
            if (oicShort) reasons.push(`${fmtNum(oicAssigned)} of ${oicRequired} OIC`);
            // No inRotation gate — the Fix Coverage modal's Cross-
            // department tab can pull from anyone in the agency, so
            // specialty shifts (Dispatch, ACO, K-9) can be covered
            // too. The modal itself handles "no eligible candidates"
            // gracefully on a per-tab basis when there's truly
            // nothing to pick from.
            // Coverage cells are always clickable for supervisors —
            // short cells open the modal to fill the gap, fully-
            // covered cells open the same modal so the supervisor
            // can still add an officer (a planned extra, a coverage
            // pre-fill for an upcoming time-off request, etc.). The
            // hover treatment differentiates: red → red hover (fix),
            // grey → slate hover (add extra).
            const canEdit = !!p.onFixCoverage && shiftActive;
            return (
              <td
                key={d.toISOString()}
                onClick={canEdit ? () => p.onFixCoverage!(group.shift!.id, ymd(d)) : undefined}
                onMouseEnter={() => p.onHoverCol(i)}
                className={cellChrome(
                  d, today, i,
                  "px-1 py-1 text-center text-[10px] font-mono " +
                  (i > 0 && d.getDay() === 0 ? "border-l-2 border-l-slate-300 " : "") +
                  (!shiftActive
                    ? "bg-slate-100 text-slate-300"
                    : short
                      ? "bg-red-100 text-red-800 font-bold"
                      : "text-slate-500") +
                  (canEdit
                    ? " cursor-pointer " + (short ? "hover:bg-red-200" : "hover:bg-slate-100")
                    : ""),
                )}
                style={i === p.hoveredCol
                  ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                  : undefined}
                title={!shiftActive
                  ? `${group.shift!.name} isn't scheduled on this date`
                  : overridden
                  ? `Marked covered (override) — raw count ${fmtNum(assigned)} of ${required}`
                  : short
                  ? (canEdit
                    ? `Click to fix — ${reasons.join("; ")}`
                    : `Not covered — ${reasons.join("; ")}`)
                  : (canEdit
                    ? `Covered ${fmtNum(assigned)} of ${required} — click to add an officer`
                    : `Covered: ${fmtNum(assigned)} of ${required}`)}
              >
                {!shiftActive ? "—" : (<>{fmtNum(assigned)}/{required}</>)}
                {shiftActive && overridden && (
                  <span
                    className="ml-1 text-[8px] font-semibold align-middle text-amber-600"
                    title="Coverage override"
                  >
                    ✓
                  </span>
                )}
              </td>
            );
          })}
        </tr>
      )}
      {group.shift && group.shift.requiresOic !== false && (
        <tr className="border-b border-slate-200">
          <td
            className={
              (p.betaDesign
                ? "sticky left-0 z-10 px-1 w-16 max-w-16 overflow-hidden "
                : "md:sticky md:left-0 z-10 px-3 ") +
              "bg-slate-50 border-r border-slate-200 py-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500"
            }
          >
            OIC
          </td>
          {days.map((d, i) => {
            const date = ymd(d);
            const cov = p.coverageByShiftDate.get(`${group.shift!.id}|${date}`);
            const oicAssigned = cov?.oicAssigned ?? 0;
            const oicRequired = cov?.oicRequired ?? 1;
            const shiftActive = p.isShiftActiveOnDate(date);
            const overridden = cov?.overridden ?? false;
            const short = shiftActive && !overridden && oicAssigned < oicRequired;
            // No inRotation gate — the Fix Coverage modal's Cross-
            // department tab can pull from anyone in the agency, so
            // specialty shifts (Dispatch, ACO, K-9) can be covered
            // too. The modal itself handles "no eligible candidates"
            // gracefully on a per-tab basis when there's truly
            // nothing to pick from.
            // OIC cell is clickable whether short or covered — the
            // modal handles "I just want to add another supervisor"
            // and "fill the missing OIC" the same way.
            const canEdit = !!p.onFixCoverage && shiftActive;
            return (
              <td
                key={d.toISOString()}
                onClick={canEdit ? () => p.onFixCoverage!(group.shift!.id, ymd(d)) : undefined}
                onMouseEnter={() => p.onHoverCol(i)}
                className={cellChrome(
                  d, today, i,
                  "px-1 py-1 text-center text-[10px] font-mono " +
                  (i > 0 && d.getDay() === 0 ? "border-l-2 border-l-slate-300 " : "") +
                  (!shiftActive
                    ? "bg-slate-100 text-slate-300"
                    : short
                      ? "bg-red-100 text-red-800 font-bold"
                      : "text-slate-500") +
                  (canEdit
                    ? " cursor-pointer " + (short ? "hover:bg-red-200" : "hover:bg-slate-100")
                    : ""),
                )}
                style={i === p.hoveredCol
                  ? { boxShadow: "inset 0 0 0 9999px var(--cell-hover)" }
                  : undefined}
                title={!shiftActive
                  ? `${group.shift!.name} isn't scheduled on this date`
                  : short
                  ? (canEdit
                    ? `Click to fix — ${fmtNum(oicAssigned)} of ${oicRequired} OIC`
                    : `No OIC: ${fmtNum(oicAssigned)} of ${oicRequired} required`)
                  : (canEdit
                    ? `OIC covered ${fmtNum(oicAssigned)} of ${oicRequired} — click to add an OIC`
                    : `OIC covered: ${fmtNum(oicAssigned)} of ${oicRequired}`)}
              >
                {!shiftActive ? "—" : (<>{fmtNum(oicAssigned)}/{oicRequired}</>)}
              </td>
            );
          })}
        </tr>
      )}
    </>
  );
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Cell-input normalization + validation. Runs on every commit
// (custom text, inline-editor typed value, bulk paste). The rule
// is intentionally strict on hour ranges so garbage like "5" or
// ambiguous 12-hour clock entries ("9-5", "2p") can't slip in:
//
//   * Empty              → clear (unchanged)
//   * Letter-first       → shift code, uppercased, accepted as-is.
//                          (SA, XXX, V, TR, OT½, S1, custom codes.)
//   * Digit-first        → MUST parse as HH-HH in 24-hour format.
//                          Auto-pads single digits ("9-14" → "09-14")
//                          and accepts wrap-midnight ranges ("22-06").
//                          Rejects a bare number, an am/pm suffix, or
//                          anything without a proper HH-HH shape.
export function normalizeCellCode(
  input: string,
): { ok: true; value: string } | { ok: false; error: string } {
  const s = input.trim();
  if (s === "") return { ok: true, value: "" };
  if (/^[A-Za-z]/.test(s)) return { ok: true, value: s.toUpperCase() };
  const cleaned = s.toUpperCase()
    .replace(/\s*HRS?$/, "")
    .replace(/\s+TO\s+/, "-")
    .replace(/\s+/g, "")
    .replace(/[–—]/g, "-");
  const m = cleaned.match(/^(\d{1,2})-(\d{1,2})$/);
  if (!m) {
    return {
      ok: false,
      error: `"${s}" isn't a valid entry. For custom hours use 24-hour format like 06-14 or 22-06 (dash between two hours). For codes, start with a letter.`,
    };
  }
  const sh = Number(m[1]);
  const eh = Number(m[2]);
  if (sh < 0 || sh > 23 || eh < 0 || eh > 23) {
    return {
      ok: false,
      error: `Hours must be 00-23 (24-hour format). Got "${s}".`,
    };
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return { ok: true, value: `${pad(sh)}-${pad(eh)}` };
}

// Shift a YYYY-MM-DD string by ±N calendar days without dragging in
// date-fns just for this. Used by the extended-range chip placement
// to hop the chip to the covering officer's home-cell date when the
// gap or their home shift wraps midnight.
function shiftYmd(dateStr: string, delta: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Group a day's cross-shift OT assignments into coverage events.
// Rows sharing the same coverage_group_id form one group (a split OT
// pair or a solo Full OT). Legacy rows without a group id each
// become their own group so they still render individually.
function groupOTByCoverage(rows: Assignment[]): Assignment[][] {
  const groups: Assignment[][] = [];
  const byGroup = new Map<string, Assignment[]>();
  for (const a of rows) {
    if (!a.coverageGroupId) {
      groups.push([a]);
      continue;
    }
    const bucket = byGroup.get(a.coverageGroupId);
    if (bucket) bucket.push(a);
    else {
      const fresh: Assignment[] = [a];
      byGroup.set(a.coverageGroupId, fresh);
      groups.push(fresh);
    }
  }
  return groups;
}

function initialsOf(e: Employee | undefined): string {
  if (!e) return "??";
  const f = (e.firstName ?? "").trim()[0] ?? "";
  const l = (e.lastName ?? "").trim()[0] ?? "";
  return (f + l).toUpperCase() || "??";
}

// A single "JJ-CS" / "JB" chip on the consolidated OT row. Pairs
// show first-half officer then second-half. We sort OT½s by
// override_reason (so "first half" comes before "second half" per
// what the Fix Coverage modal writes); if both rows happen to be
// OT½s with no distinguishing reason, fall back to createdAt order.
function OTGroupChip({
  rows, employeesById, onDeleteOt,
}: {
  rows: Assignment[];
  employeesById: Map<string, Employee>;
  onDeleteOt?: (a: Assignment) => void;
}) {
  const ordered = [...rows].sort((a, b) => {
    const ar = (a.overrideReason ?? "").toLowerCase();
    const br = (b.overrideReason ?? "").toLowerCase();
    const aFirst = ar.includes("first half") ? 0 : ar.includes("second half") ? 2 : 1;
    const bFirst = br.includes("first half") ? 0 : br.includes("second half") ? 2 : 1;
    if (aFirst !== bFirst) return aFirst - bFirst;
    return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
  });
  const isPair = ordered.length > 1;
  const label = ordered.map((a) => initialsOf(employeesById.get(a.employeeId))).join("-");
  const names = ordered
    .map((a) => {
      const e = employeesById.get(a.employeeId);
      return e ? `${e.firstName} ${e.lastName}` : "Unknown";
    })
    .join(isPair ? " + " : "");
  const codeLabel = isPair ? "OT split" : "OT full";
  return (
    <div
      className="w-full h-full flex items-center justify-center gap-1 bg-amber-100 text-amber-800 text-[11px] font-semibold leading-none select-none relative"
      title={`${codeLabel} · ${names}`}
    >
      {label}
      {onDeleteOt && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); ordered.forEach((a) => onDeleteOt(a)); }}
          className="absolute right-0.5 text-amber-500 hover:text-amber-900 leading-none text-[10px] print:hidden"
          title="Remove this coverage"
        >
          ×
        </button>
      )}
    </div>
  );
}

// One cell's visual chip. Fills the cell completely — the background
// colour of any "off" / "partial" code paints the entire square so
// shortages read at a glance.
function CellChip({
  note, codeMeta, draggable, onDragStart,
}: {
  note: string;
  codeMeta: ShiftCode | undefined;
  draggable: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
}) {
  const base =
    "w-full h-full flex items-center justify-center text-[11px] font-semibold leading-none select-none";
  if (!note) {
    return <div className={base + " text-transparent"}>·</div>;
  }
  if (codeMeta) {
    // OT / OT½ get the same soft amber treatment as the
    // consolidated OT row chip ("JA", "BS-CS", …) so a cell-level
    // OT and the row-level OT visually belong to the same family.
    // The raw shift_codes.color (#f59e0b — strong orange) was
    // landing as a solid block that read louder than the OT row.
    const isOtCode =
      codeMeta.code === "OT" || codeMeta.code === "OT½"
      || codeMeta.code === "OT/2";
    if (isOtCode) {
      return (
        <div
          className={base + " bg-amber-100 text-amber-800"}
          title={codeMeta.label || codeMeta.code}
          draggable={draggable}
          onDragStart={onDragStart}
        >
          {codeMeta.code}
        </div>
      );
    }
    // Status codes render transparent so the row reads quietly —
    // only the generated overtime chips (handled elsewhere, not
    // through CellChip) paint a background. An explicit per-code
    // colour configured in Settings still wins, for agencies that
    // want to flag specific codes.
    const bg = codeMeta.color ?? "transparent";
    return (
      <div
        className={base + " text-slate-700"}
        style={{ backgroundColor: bg }}
        title={codeMeta.label || codeMeta.code}
        draggable={draggable}
        onDragStart={onDragStart}
      >
        {codeMeta.code}
      </div>
    );
  }
  // Free-text custom code (e.g. "9-4", "124"). Transparent too.
  return (
    <div
      className={base + " text-slate-800"}
      title={note}
      draggable={draggable}
      onDragStart={onDragStart}
    >
      {note}
    </div>
  );
}

// ---- Empty slot row ----------------------------------------------------

// ---- Code-picker popover -----------------------------------------------

const CodePickerPopover = forwardRef<HTMLDivElement, {
  anchor: DOMRect;
  employee: Employee;
  date: string;
  codes: ShiftCode[];
  existing: Assignment | null;
  // Non-null when the cell is currently showing an approved
  // time-off overlay (no assignment). Enables the "Remove" button
  // and the "Approved time off" status label so the supervisor can
  // edit the chip the same way as a regular assignment chip.
  timeOffCode: string | null;
  selectionSize: number;
  // Return-a-result variants so the picker can surface validation
  // failures IN-FORM (red banner at the top of the popover) and
  // stay open, instead of the schedule-level flash banner that
  // slammed the popover shut on the first typo.
  onPick: (code: string) => { ok: true } | { ok: false; error: string };
  onClear: () => { ok: true } | { ok: false; error: string };
  onClose: () => void;
  // Only set when the current user has viewAudit permission. When
  // present, the popover shows a "View change history" link at the
  // bottom that jumps to the Audit page pre-filtered to this cell.
  onViewHistory?: () => void;
}>(function CodePickerPopover(
  { anchor, employee, date, codes, existing, timeOffCode, selectionSize,
    onPick, onClear, onClose, onViewHistory }, ref,
) {
  const [custom, setCustom] = useState("");
  const [pickerError, setPickerError] = useState<string | null>(null);
  // Wraps the parent-provided callbacks: on success close the
  // popover; on failure paint the error inline and keep the
  // popover open so the supervisor can fix the entry without
  // reopening from the grid.
  function submitPick(code: string) {
    const r = onPick(code);
    if (!r.ok) { setPickerError(r.error); return; }
    setPickerError(null);
    onClose();
  }
  function submitClear() {
    const r = onClear();
    if (!r.ok) { setPickerError(r.error); return; }
    setPickerError(null);
    onClose();
  }

  // Track the visual viewport so the popover can stay centered when
  // iOS' soft keyboard pops up. visualViewport.offsetTop/height
  // reflect the visible area shrinking from the bottom; pinning the
  // popover's `top` to offsetTop + margin keeps it inside the
  // visible slice instead of sliding behind the keyboard.
  const [vp, setVp] = useState(readVp);
  useEffect(() => {
    const v = typeof window !== "undefined" ? window.visualViewport : null;
    const update = () => setVp(readVp());
    v?.addEventListener("resize", update);
    v?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      v?.removeEventListener("resize", update);
      v?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const MARGIN = 12;
  const isMobile = vp.width < 640;

  // Anchor parameter is still accepted (used to be the cell rect we
  // hung off), but the popover is now centered on the viewport at
  // every breakpoint so the full code list is always reachable
  // without scrolling the card off-screen or cropping the bottom
  // of the menu. void it explicitly so the linter doesn't complain
  // about the prop being unused.
  void anchor;

  // Wrapper covers the visible viewport (visualViewport-aware so
  // iOS's keyboard shrinks it to the visible slice instead of the
  // full window). Flex centers the card inside that slice — mobile
  // pins to the top to keep the soft keyboard from shoving the
  // custom-code input behind itself.
  const width = Math.min(vp.width - 2 * MARGIN, 440);
  const maxH = Math.max(240, vp.height - 2 * MARGIN);

  const bulk = selectionSize > 1;
  return createPortal(
    <div
      // Full-viewport positioning wrapper. Flex centers the card
      // inside the visible viewport slice — using visualViewport
      // bounds so iOS's keyboard correctly shrinks the centering
      // area instead of leaving the card behind the keyboard.
      // Click on the empty space outside the card closes it.
      className="fixed z-[999] flex justify-center"
      style={{
        top: vp.offsetTop,
        left: vp.offsetLeft,
        width: vp.width,
        height: vp.height,
        padding: MARGIN,
        alignItems: isMobile ? "flex-start" : "center",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Soft backdrop tint behind everything in the wrapper so the
          schedule grid recedes while the picker is open. Sits in
          the same fixed layer at lower opacity than the click
          target above. */}
      <div className="fixed inset-0 -z-10 bg-black/30" aria-hidden />
      <div
        ref={ref}
        className="bg-white border border-slate-200 shadow-xl rounded-2xl overflow-auto"
        style={{ width, maxHeight: maxH }}
        role="dialog"
      >
      <div className="px-5 py-4 border-b border-slate-100 text-sm flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {bulk ? (
            <>
              <div className="font-semibold text-sky-700">
                Bulk edit — {selectionSize} cells selected
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Picking a code applies it to every selected cell.
              </div>
            </>
          ) : (
            <>
              <div className="font-medium text-slate-800 truncate">
                {employee.rank ? `${employee.rank}. ` : ""}{employee.lastName}, {employee.firstName}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{date}</div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 -mr-1.5 -mt-1"
        >
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      <div className="p-4 space-y-3">
        {pickerError && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2">
            <span className="font-semibold mt-px">Fix and try again:</span>
            <span className="flex-1">{pickerError}</span>
            <button
              type="button"
              onClick={() => setPickerError(null)}
              className="text-red-400 hover:text-red-700 font-semibold leading-none"
              aria-label="Dismiss"
            >×</button>
          </div>
        )}
        {existing ? (
          <button
            onClick={submitClear}
            className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 flex items-center gap-2"
          >
            <span className="inline-block w-4 h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] leading-none">×</span>
            Remove code
            <span className="ml-auto font-mono text-xs text-red-500">
              {(existing.note ?? "").trim().toUpperCase() || "—"}
            </span>
          </button>
        ) : timeOffCode ? (
          <button
            onClick={submitClear}
            className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 flex items-center gap-2"
            title="Drop this day out of the approved time-off range."
          >
            <span className="inline-block w-4 h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[11px] leading-none">×</span>
            Remove time off
            <span className="ml-auto font-mono text-xs text-red-500">
              {timeOffCode}
            </span>
          </button>
        ) : (
          <div className="px-3 py-2 text-sm text-emerald-700 font-medium border border-emerald-200 bg-emerald-50 rounded-lg flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-600" />
            Currently working
          </div>
        )}

        {codes.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <div className="px-1 pb-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Off / leave codes
            </div>
            <div className="space-y-0.5">
            {codes.map((c) => {
              const activeNote = (existing?.note ?? timeOffCode ?? "")
                .trim().toUpperCase();
              const selected = activeNote === c.code.toUpperCase();
              return (
                <button
                  key={c.id}
                  onClick={() => submitPick(c.code)}
                  className={
                    "w-full text-left px-3 py-2 rounded-lg text-sm flex items-baseline gap-3 hover:bg-slate-100 " +
                    (selected ? "bg-sky-50" : "")
                  }
                >
                  <span className="font-mono font-semibold text-slate-800 w-12">{c.code}</span>
                  <span className="text-xs text-slate-500 truncate">{c.label}</span>
                  {selected && <span className="ml-auto text-xs text-sky-600">●</span>}
                </button>
              );
            })}
            </div>
          </div>
        )}

        <div className="border-t border-slate-100 pt-3">
          <label className="block px-1 pb-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Custom (letters → code, e.g. SCU · digits → 24h hours, e.g. 06-14)
          </label>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (custom.trim()) submitPick(custom);
            }}
          >
            <input
              // Autofocus only when the device has a real (hardware)
              // keyboard. On touch devices, autofocus would pop the
              // soft keyboard the moment the picker opens — which
              // shoves the code list off-screen and forces iOS's
              // input-accessory toolbar over the bottom of the page.
              // The custom input is still tappable to focus when the
              // officer actually wants to type a custom code.
              autoFocus={hasHardwareKeyboard()}
              value={custom}
              onChange={(e) => { setCustom(e.target.value); if (pickerError) setPickerError(null); }}
              maxLength={8}
              className="flex-1 text-sm font-mono px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="e.g. 9-4"
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <button
              type="submit"
              disabled={!custom.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40"
            >
              Set
            </button>
          </form>
        </div>

        {/* Cell audit link. Only rendered when the parent passed a
          * handler (which it only does for users with viewAudit).
          * Sits below the custom-code entry so it's out of the way
          * but always discoverable at the bottom of the picker. */}
        {onViewHistory && (
          <div className="border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={onViewHistory}
              className="w-full text-left px-1 py-1 text-xs text-slate-500 hover:text-slate-800 hover:underline flex items-center gap-1.5"
              title="See every change made to this cell in the audit log"
            >
              <span aria-hidden>🕒</span>
              View change history for this cell →
            </button>
          </div>
        )}
      </div>
    </div>
    </div>,
    document.body,
  );
});

// ----------------------------------------------------------------------
// InlineCellEditor — a small text input that overlays a single cell
// with a typeahead dropdown of shift codes. This is what fires when
// a supervisor double-clicks a cell: they can just type, see
// matching codes filter live, hit Enter to commit. Free-text custom
// values (e.g. "9-4", "124") also commit so off-roster codes still
// work without round-tripping through the popover.
//
// Keyboard model is intentionally spreadsheet-flavoured:
//   * Empty + Enter        → clear the cell
//   * Typed + Arrow + Enter → commit the highlighted suggestion
//   * Typed + Enter (no    → commit the typed text verbatim as a
//     highlight)             custom code
//   * Escape               → cancel without writing
//   * Click outside        → commit current text
//
// Filtering matches against both the code letters and the human
// label, case-insensitive, so typing "vac" surfaces "V — Vacation"
// just like typing "v" does.
function InlineCellEditor({
  anchor, codes, seed, onCommit, onClear, onClose,
}: {
  anchor: DOMRect;
  codes: ShiftCode[];
  seed: string;
  // Return-a-result callbacks: on failure the editor paints the
  // error just below the input and stays open so the supervisor
  // can fix and retry without reopening from the grid.
  onCommit: (value: string) => { ok: true } | { ok: false; error: string };
  onClear: () => { ok: true } | { ok: false; error: string };
  onClose: () => void;
}) {
  const [value, setValue] = useState(seed);
  const [highlight, setHighlight] = useState(0);
  const [editorError, setEditorError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return codes.slice(0, 24);
    // Rank: prefix match on code first, then prefix match on
    // label, then substring fallback. That way typing "v" puts
    // "V — Vacation" above "S — Sick" even if Sick's label
    // happens to contain a v somewhere.
    const score = (c: ShiftCode): number => {
      const code = c.code.toLowerCase();
      const label = c.label.toLowerCase();
      if (code === q) return 0;
      if (code.startsWith(q)) return 1;
      if (label.startsWith(q)) return 2;
      if (code.includes(q)) return 3;
      if (label.includes(q)) return 4;
      return 99;
    };
    return codes
      .map((c) => ({ c, s: score(c) }))
      .filter((x) => x.s < 99)
      .sort((a, b) => a.s - b.s || a.c.code.localeCompare(b.c.code))
      .map((x) => x.c)
      .slice(0, 24);
  }, [value, codes]);

  // Reset highlight to top whenever filtered list changes so the
  // current "top match" is what an immediate Enter would commit.
  useEffect(() => { setHighlight(0); }, [suggestions]);

  // Click outside the editor commits whatever's typed. Matches the
  // spreadsheet pattern — abandoning the cell is a save action.
  // If the commit fails validation, keep the editor open so the
  // supervisor sees + fixes the error in place.
  useEffect(() => {
    function away(ev: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(ev.target as Node)) {
        const v = value.trim();
        if (v === seed.trim()) { onClose(); return; }
        const r = onCommit(v);
        if (!r.ok) { setEditorError(r.error); return; }
        onClose();
      }
    }
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [value, seed, onCommit, onClose]);

  useEffect(() => {
    // Select-all on mount so the supervisor can overwrite the
    // existing code immediately by typing, or accept it as-is with
    // Enter.
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Positioning: anchored to the cell, sized to it, dropdown
  // hangs below. Falls back above if there's no room beneath.
  const MARGIN = 6;
  const DROPDOWN_H = 220;
  const vpH = typeof window !== "undefined" ? window.innerHeight : 800;
  const placeBelow = anchor.bottom + DROPDOWN_H + MARGIN < vpH;
  // Offset of the dropdown's top edge relative to the outer
  // wrapper (which is pinned to the cell's top-left). Below: just
  // past the input's bottom. Above: lift it up by its own height.
  const dropdownTop = placeBelow
    ? anchor.height + 2
    : -DROPDOWN_H - 2;

  function commitHighlighted() {
    const v = value.trim();
    const r = v === ""
      ? onClear()
      : (suggestions[highlight]
        ? onCommit(suggestions[highlight].code)
        : onCommit(v));
    if (!r.ok) { setEditorError(r.error); return; }
    onClose();
  }

  return createPortal(
    <div
      ref={boxRef}
      className="fixed z-[1000]"
      style={{ left: anchor.left, top: anchor.top }}
    >
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => { setValue(e.target.value); if (editorError) setEditorError(null); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitHighlighted();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === "Tab") {
            // Tab acts like Enter — commit and let the surrounding
            // grid handle focus movement.
            e.preventDefault();
            commitHighlighted();
          }
        }}
        maxLength={16}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        className="font-mono uppercase text-center text-xs px-1 bg-white border-2 border-sky-500 outline-none rounded-sm shadow"
        style={{
          width: Math.max(anchor.width, 64),
          height: anchor.height,
        }}
      />
      {editorError && (
        <div
          className="absolute bg-red-50 border border-red-300 rounded-md shadow-xl px-3 py-2 text-xs text-red-800 flex items-start gap-2"
          style={{
            top: dropdownTop,
            left: 0,
            minWidth: Math.max(anchor.width, 260),
            maxWidth: 360,
          }}
        >
          <span className="font-semibold shrink-0">Try again:</span>
          <span className="flex-1">{editorError}</span>
        </div>
      )}
      <div
        className="absolute mt-0.5 bg-white border border-slate-200 rounded-md shadow-xl overflow-auto"
        style={{
          // When the error banner is showing, drop the suggestion
          // list below it so both are readable.
          top: editorError ? dropdownTop + 44 : dropdownTop,
          left: 0,
          minWidth: Math.max(anchor.width, 220),
          maxHeight: DROPDOWN_H,
        }}
      >
        {suggestions.length === 0 ? (
          <div className="px-3 py-2 text-xs text-slate-500">
            No matching code — press Enter to save{" "}
            <span className="font-mono font-semibold">
              "{value.trim().toUpperCase()}"
            </span>{" "}
            as a custom note.
          </div>
        ) : (
          <ul className="py-1">
            {suggestions.map((c, i) => (
              <li key={c.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    // mousedown beats the outside-click handler so
                    // the click actually commits this suggestion
                    // instead of saving the typed value.
                    e.preventDefault();
                    onCommit(c.code);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={
                    "w-full text-left px-3 py-1 text-sm flex items-baseline gap-2 "
                    + (i === highlight ? "bg-sky-50" : "hover:bg-slate-50")
                  }
                >
                  <span className="font-mono font-semibold text-slate-800 w-10">
                    {c.code}
                  </span>
                  <span className="text-xs text-slate-500 truncate">{c.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Inline "+ Add officer" button that opens a small dropdown of employees
// not currently rostered on this shift.
//
// The menu uses FIXED positioning (not absolute inside the button's
// relative wrapper) so it floats above the table regardless of sticky
// cells and stacking contexts created by sibling rows. Without this,
// the menu gets obscured by the next shift section's rows.
function AddOfficerButton({
  shift, addable, onPick,
}: {
  shift: Shift;
  addable: Employee[];
  onPick: (emp: Employee) => void;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!anchor) return;
    function away(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setAnchor(null);
    }
    function esc(e: KeyboardEvent) { if (e.key === "Escape") setAnchor(null); }
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [anchor]);

  function toggle() {
    if (anchor) { setAnchor(null); return; }
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) setAnchor(rect);
  }

  // Same flip-and-cap logic as the cell picker so a tap on the
  // "+ Add officer" button near the bottom of a phone viewport
  // doesn't render most of the menu off-screen.
  const MARGIN = 8;
  const MENU_W = 256;
  const MENU_DESIRED_H = 256;
  const spaceBelow = anchor ? window.innerHeight - anchor.bottom - MARGIN : 0;
  const spaceAbove = anchor ? anchor.top - MARGIN : 0;
  const placeBelow = spaceBelow >= 200 || spaceBelow >= spaceAbove;
  const menuMaxH = anchor
    ? Math.max(160, Math.min(MENU_DESIRED_H, placeBelow ? spaceBelow : spaceAbove))
    : MENU_DESIRED_H;
  const menuTop = anchor
    ? (placeBelow ? anchor.bottom + 4 : Math.max(MARGIN, anchor.top - 4 - menuMaxH))
    : 0;
  const menuLeft = anchor
    ? Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - MENU_W - MARGIN))
    : 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className="text-[11px] text-slate-500 hover:text-sky-700 hover:bg-sky-50 px-2 py-1 rounded border border-dashed border-slate-300"
        title={`Add an officer to ${shift.name}`}
      >
        + Add officer
      </button>
      {anchor && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[1000] w-64 overflow-auto bg-white border border-slate-200 shadow-xl rounded"
          style={{ top: menuTop, left: menuLeft, maxHeight: menuMaxH }}
        >
          {addable.length === 0 ? (
            <div className="p-3 text-xs text-slate-500 italic">
              No other active employees available.
            </div>
          ) : (
            addable.map((e) => (
              <button
                key={e.id}
                onClick={() => { onPick(e); setAnchor(null); }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              >
                <span className="font-medium text-slate-800">
                  {e.rank ? `${e.rank}. ` : ""}{e.lastName}, {e.firstName}
                </span>
                {(e.radioNumber ?? e.badgeNumber) && (
                  <span className="text-slate-400 ml-2">#{e.radioNumber ?? e.badgeNumber}</span>
                )}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

// ---- Helpers -----------------------------------------------------------

// Pay-period weeks to tint (set via Settings; defaults to [0, 2]).
let payWeekendWeeksForChrome: number[] = [0, 2];
export function setPayWeekendWeeksForChrome(v: number[]) {
  payWeekendWeeksForChrome = v;
}

function cellChrome(d: Date, today: Date, dayIndex: number, extra = "") {
  const isToday = isSameYmd(d, today);
  const day = d.getDay();
  // Pay-period markers. Each entry in payWeekendWeeksForChrome
  // identifies the START week of a biweekly pay period (0-indexed
  // against the 4-week schedule window). For every such week W we
  // mark two cells:
  //   * the START Sunday of the pay period — dayIndex = W*7
  //   * the END Saturday of the pay period — dayIndex = W*7 + 13
  //     (two weeks later, minus one to land on the closing Sat)
  // So picking weeks 1 + 3 (the default) lights up the four
  // bookend days of the two biweekly periods: Sun (1st), Sat
  // (end of week 2), Sun (start of week 3), Sat (end of week 4).
  let inPayWeekend = false;
  for (const w of payWeekendWeeksForChrome) {
    if (day === 0 && dayIndex === w * 7) { inPayWeekend = true; break; }
    if (day === 6 && dayIndex === w * 7 + 13) { inPayWeekend = true; break; }
  }
  return [
    inPayWeekend ? "bg-amber-50" : "",
    // Today's column gets the same full-cell overlay treatment as
    // the mouse-hover highlight, but green. cell-today is a single
    // class defined in styles.css (inset box-shadow) so it
    // composites over whatever background the cell carries.
    isToday ? "cell-today" : "",
    extra,
  ].filter(Boolean).join(" ");
}

// "HH:MM" + minutes → "HH:MM", wrapping around midnight.
function shiftEndTime(start: string, durationMin: number): string {
  const [h, m] = start.split(":").map((n) => Number(n));
  const total = ((h || 0) * 60 + (m || 0) + durationMin) % (24 * 60);
  const eh = Math.floor(total / 60);
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

// True only when the user-agent reports a fine pointer + hover —
// i.e. an actual mouse / trackpad with a hardware keyboard. Touch
// devices report (hover: none) and (pointer: coarse), even tablets
// with a stylus. Used to gate features that play badly with the
// soft keyboard, like autofocusing an input on popover open.
function hasHardwareKeyboard(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(hover: hover) and (pointer: fine)").matches
    ?? false;
}

// Snapshot the visible viewport — width/height of the area that is
// not covered by the on-screen keyboard, plus its offset within
// the layout viewport. Falls back to window.innerWidth/Height on
// older Safaris and during SSR. Subscribers re-read this on the
// VisualViewport's resize/scroll events to keep modal-style
// popovers centered when iOS shifts the page to bring a focused
// input into view.
function readVp(): {
  width: number; height: number; offsetTop: number; offsetLeft: number;
} {
  if (typeof window === "undefined") {
    return { width: 0, height: 0, offsetTop: 0, offsetLeft: 0 };
  }
  const v = window.visualViewport;
  return {
    width: v?.width ?? window.innerWidth,
    height: v?.height ?? window.innerHeight,
    offsetTop: v?.offsetTop ?? 0,
    offsetLeft: v?.offsetLeft ?? 0,
  };
}

